import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";

// Month-card icon — drops in from above with a staggered cascade so
// the row of 12 cards reads left-to-right when the page mounts. Total
// run ≈ 0.6 s: each icon's animation lasts 280 ms and starts 28 ms
// after the previous one (11 × 28 ms + 280 ms ≈ 588 ms). The fade-in
// is baked into the same keyframe so opacity and transform stay in
// lockstep instead of fighting each other.
const MONTH_SLUGS = [
  "january","february","march","april","may","june",
  "july","august","september","october","november","december",
];
const MI_DROP_MS = 280;
const MI_STAGGER_MS = 28;
function MonthIcon({ index, isSel, isCur }) {
  const finalOpacity = isSel ? 1 : 0.92;
  const src = `/icons/months/${String(index + 1).padStart(2, "0")}-${MONTH_SLUGS[index]}.svg`;
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      decoding="async"
      loading="eager"
      style={{
        width: "100%",
        aspectRatio: "1 / 1",
        marginTop: "-50%",
        objectFit: "contain",
        flexShrink: 0,
        pointerEvents: "none",
        filter: isSel || isCur ? "none" : "saturate(0.92)",
        marginBottom: 2,
        // Animation handles both fade-in (opacity 0 → --mi-final-opacity)
        // and the drop (translateY -72 px → 0, scale 0.85 → 1). The
        // cubic-bezier overshoots slightly so the icon settles with a
        // tiny bounce. animation-fill-mode: both holds the initial state
        // until the per-icon delay elapses.
        opacity: 0,
        animation: `monthIconDrop ${MI_DROP_MS}ms cubic-bezier(0.34, 1.45, 0.64, 1) ${index * MI_STAGGER_MS}ms both`,
        "--mi-final-opacity": finalOpacity,
      }}
    />
  );
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const h = e => setMobile(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return mobile;
}
import { Card, Label, Btn, Inp, DateInp, Info, Tooltip, Modal, SlidePanel, BottomSheet, ConfirmSheet } from "../components/ui.jsx";
import MktSelector from "../components/MktSelector.jsx";
import { LS } from "../lib/storage.js";
import AddTransactionModal from "../components/AddTransactionModal.jsx";
import ReminderModal from "../components/ReminderModal.jsx";
import ExportModal from "../components/ExportModal.jsx";
import AttachmentButton from "../components/AttachmentButton.jsx";
import { FONT, RADIUS, ORDER_COLORS, SPRING } from "../lib/theme.js";
import { escHtml } from "../lib/safeguards.js";
import { formatDate, formatDateTime, getDateFormat } from "../lib/format.js";
import {
  fmt, fmtFull, mkKey, MONTH_NAMES, getDaysInMonth, currSym,
  computeDeliveryExpenses, generateOrderExpenses, generateRecurringItems,
  generateDecisionRecurringItems, generateDecisionOneTimeItems, generateAdSpendExpenses,
  getMonthlyPayoutBreakdown, applyModifiers, uid,
} from "../lib/calculations.js";

const MKT_COLORS = ["#0071e3","#bf5af2","#34c759","#ff9500","#ff3b30","#5ac8fa"];

// ─── Delay Modal ──────────────────────────────────────────────────────────────
function DelayModal({ open, itemLabel, onClose, onDelay }) {
  const T = useTheme();
  const [mode, setMode] = useState("date"); // "date"|"days"|"months"
  const [date, setDate] = useState("");
  const [days, setDays] = useState("");
  const [mons, setMons] = useState("");

  function submit() {
    if (mode === "date" && date) onDelay({ type: "date", value: date });
    else if (mode === "days" && days) onDelay({ type: "days", value: parseInt(days) });
    else if (mode === "months" && mons) onDelay({ type: "months", value: parseInt(mons) });
    onClose();
  }

  const delayFooter = (
    <>
      <Btn onClick={onClose} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn>
      <Btn onClick={submit} color={T.orange} style={{flex:2}}
        disabled={(mode==="date"&&!date)||(mode==="days"&&!days)||(mode==="months"&&!mons)}>
        Move
      </Btn>
    </>
  );

  return (
    <BottomSheet open={open} onClose={onClose} title="📅 Move Transaction" height="auto" footer={delayFooter}>
      <div style={{ display:"flex", flexDirection:"column", gap:16, paddingBottom:4 }}>
        <div style={{ fontSize:13,color:T.textSub }}>"{itemLabel}" will move to a new date.</div>
        <div style={{ display:"flex",gap:8 }}>
          {[{id:"date",l:"📅 Date"},{id:"days",l:"➕ Days"},{id:"months",l:"📆 Months"}].map(m=>(
            <button key={m.id} onClick={()=>setMode(m.id)} style={{ flex:1,padding:"8px 4px",borderRadius:RADIUS.md,fontFamily:FONT,fontSize:12,fontWeight:mode===m.id?700:400,cursor:"pointer",background:mode===m.id?T.blue+"20":"transparent",color:mode===m.id?T.blue:T.textSub,border:`1.5px solid ${mode===m.id?T.blue:T.border}` }}>{m.l}</button>
          ))}
        </div>
        {mode === "date" && <div><Label style={{ marginBottom:8 }}>New date</Label><DateInp value={date} onChange={setDate} placeholder="Select date"/></div>}
        {mode === "days" && <div><Label style={{ marginBottom:8 }}>Move by days</Label><div style={{ display:"flex",alignItems:"center",gap:8 }}><input type="number" value={days} onChange={e=>setDays(e.target.value)} placeholder="e.g. 14" min={1} style={{ background:T.surface,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.md,color:T.text,padding:"9px 14px",fontSize:15,width:120,outline:"none",fontFamily:FONT }}/><span style={{ color:T.textSub,fontSize:14 }}>days</span></div></div>}
        {mode === "months" && <div><Label style={{ marginBottom:8 }}>Move by months</Label><div style={{ display:"flex",alignItems:"center",gap:8 }}><input type="number" value={mons} onChange={e=>setMons(e.target.value)} placeholder="e.g. 1" min={1} style={{ background:T.surface,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.md,color:T.text,padding:"9px 14px",fontSize:15,width:120,outline:"none",fontFamily:FONT }}/><span style={{ color:T.textSub,fontSize:14 }}>months</span></div></div>}
      </div>
    </BottomSheet>
  );
}

// ─── Compute delayed date ─────────────────────────────────────────────────────
function computeDelayedDate(originalDate, delayAction) {
  if (!delayAction) return originalDate;
  if (delayAction.type === "date") return delayAction.value;
  const base = originalDate ? (() => {
    const p = originalDate.split(".");
    if (p.length === 3) return new Date(parseInt(p[2]), parseInt(p[1])-1, parseInt(p[0]));
    return new Date();
  })() : new Date();
  if (delayAction.type === "days") {
    const d = new Date(base); d.setDate(d.getDate() + delayAction.value);
    return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
  }
  if (delayAction.type === "months") {
    const d = new Date(base);
    const origDay = d.getDate();
    const targetMonth = d.getMonth() + delayAction.value;
    d.setDate(1); // avoid overflow during setMonth
    d.setMonth(targetMonth);
    // Clamp to last day of target month (e.g. Jan 31 + 1 month = Feb 28, not Mar 3)
    const maxDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(origDay, maxDay));
    return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
  }
  return originalDate;
}

// ─── Action row shown below a line item ──────────────────────────────────────
function ActionRow({ catType, item, mData, months, selectedMonth, monthOps, onDelayClick, T, orders, onNavigate, recurringExpenseOps, recurringIncomeOps, onConfirmDelete, onMarkPaid }) {
  const isMobile = useIsMobile();
  const [showRecDel, setShowRecDel] = useState(false);
  const [showOrdDel, setShowOrdDel] = useState(false);

  if (catType === "delivery") {
    const ov = (mData.deliveryOverrides || {})[item.id] || {};
    return (
      <>
      <div style={{ display:"flex",gap:8,padding:isMobile?"6px 10px":"8px 32px 10px",background:"transparent",borderBottom:`1px solid ${T.border}33`,flexWrap:"wrap",alignItems:"center" }}>
        {!ov.paid && <><Btn onClick={()=>onDelayClick(item.id,"delivery",item)} size="xs" color={T.orange} variant="outline">📅 Move</Btn>
        {ov.partialAmount !== undefined
          ? <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
              <span style={{fontSize:12,color:T.textSub}}>Paid so far: {currSym()}</span>
              <input type="number" value={ov.partialAmount||""} placeholder="0" onChange={e=>monthOps.updateDeliveryOverride(selectedMonth,item.id,{partialAmount:parseFloat(e.target.value)||0})} style={{background:T.surface,border:`1px solid ${T.orange}`,borderRadius:RADIUS.sm,color:T.orange,padding:"3px 7px",fontSize:12,width:90,outline:"none",fontFamily:FONT,fontWeight:700}}/>
              <Btn onClick={()=>monthOps.updateDeliveryOverride(selectedMonth,item.id,{partialAmount:undefined})} size="xs" variant="ghost" color={T.textSub}>Reset</Btn>
            </div>
          : <Btn onClick={()=>monthOps.updateDeliveryOverride(selectedMonth,item.id,{partialAmount:0})} size="xs" color={T.orange} variant="outline">Pay Partially</Btn>
        }
        </> }
        {!ov.deleted && <Btn onClick={()=>onConfirmDelete({label:item.name||"delivery expense",action:()=>monthOps.updateDeliveryOverride(selectedMonth,item.id,{deleted:true,paid:false})})} size="xs" color={T.red} variant="outline" danger>Delete</Btn>}
        {ov.deleted && <Btn onClick={()=>monthOps.updateDeliveryOverride(selectedMonth,item.id,{deleted:false})} size="xs" color={T.green} variant="outline">↩ Restore</Btn>}
        <button onClick={()=>onNavigate?.("marketplaces",{})} style={{
          background:"none", border:"none", color:T.textSub, cursor:"pointer",
          fontSize:12, fontFamily:FONT, padding:"2px 6px", borderRadius:RADIUS.sm,
          textDecoration:"underline", marginLeft:"auto",
        }}>⚙ Configure in Marketplaces →</button>
      </div>
      <div style={{ padding:isMobile?"4px 10px":"6px 32px 10px", background:"transparent", borderBottom:`1px solid ${T.border}33`,
        fontSize:12, color:T.textSub, lineHeight:1.7 }}>
        <span style={{ color:T.orange, fontWeight:600 }}>ℹ Fulfilment costs</span> are calculated from
        Marketplace product settings × monthly unit targets.
        To change the per-unit cost: <strong>Marketplaces → product → Delivery/unit</strong>.
      </div>
      </>
    );
  }

  if (catType === "orders") {
    const isPaid = (mData.orderExpPaid||{})[item.id]||false;
    const partial = (mData.orderExpPartial||{})[item.id];
    return (
      <>
        <OrderTxDeleteDialog open={showOrdDel} item={item} orders={orders} onClose={()=>setShowOrdDel(false)}
          onDeleteTx={()=>{ const u={...months,[selectedMonth]:{...months[selectedMonth],orderExpPaid:{...(months[selectedMonth]?.orderExpPaid||{}),[item.id]:true}}}; monthOps.setMonthsDirect?.(u); }}
          onDelayTx={()=>onDelayClick(item.id,"orders",item)}
          onNavigateOrder={onNavigate?()=>onNavigate("orders",{orderId:item.orderId}):null}
          months={months} selectedMonth={selectedMonth} monthOps={monthOps}
        />
        <div style={{ display:"flex",gap:8,padding:isMobile?"6px 10px":"8px 32px 10px",background:"transparent",borderBottom:`1px solid ${T.border}33`,flexWrap:"wrap",alignItems:"center" }}>
          {!isPaid && <><Btn onClick={()=>onDelayClick(item.id,"orders",item)} size="xs" color={T.orange} variant="outline">📅 Move</Btn>
          {partial !== undefined
            ? <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontSize:12,color:T.textSub}}>Paid so far: {currSym()}</span>
                <input type="number" value={partial||""} placeholder="0" onChange={e=>{const u={...months,[selectedMonth]:{...months[selectedMonth],orderExpPartial:{...(months[selectedMonth]?.orderExpPartial||{}),[item.id]:parseFloat(e.target.value)||0}}};monthOps.setMonthsDirect?.(u);}} style={{background:T.surface,border:`1px solid ${T.orange}`,borderRadius:RADIUS.sm,color:T.orange,padding:"3px 7px",fontSize:12,width:90,outline:"none",fontFamily:FONT,fontWeight:700}}/>
                <Btn onClick={()=>{const u={...months,[selectedMonth]:{...months[selectedMonth],orderExpPartial:{...(months[selectedMonth]?.orderExpPartial||{}),[item.id]:undefined}}};monthOps.setMonthsDirect?.(u);}} size="xs" variant="ghost" color={T.textSub}>Reset</Btn>
              </div>
            : <Btn onClick={()=>{const u={...months,[selectedMonth]:{...months[selectedMonth],orderExpPartial:{...(months[selectedMonth]?.orderExpPartial||{}),[item.id]:0}}};monthOps.setMonthsDirect?.(u);}} size="xs" color={T.orange} variant="outline">Pay Partially</Btn>
          }
          </> }
          <Btn onClick={()=>setShowOrdDel(true)} size="xs" color={T.red} variant="outline" danger>Delete…</Btn>
        </div>
      </>
    );
  }

  if (catType === "recurring_exp" || catType === "recurring_inc") {
    const ov=(mData.recurringOverrides||{})[item.id]||{};
    const amt=ov.amount!==undefined?ov.amount:item.amount;
    const isExpRec = catType === "recurring_exp";
    return (
      <>
        <RecurringDeleteDialog open={showRecDel} itemName={item.what||item.name||"Transaction"} onClose={()=>setShowRecDel(false)}
          onDeleteThis={()=>{ monthOps.updateRecurringOverride(selectedMonth,item.id,{deleted:true,paid:false}); setShowRecDel(false); }}
          onDeleteAll={()=>{
            if (isExpRec && recurringExpenseOps) recurringExpenseOps.delete(item.recurringId);
            else if (!isExpRec && recurringIncomeOps) recurringIncomeOps.delete(item.recurringId);
            setShowRecDel(false);
          }}
        />
        <div style={{ display:"flex",gap:8,padding:isMobile?"6px 10px":"8px 32px 10px",background:"transparent",borderBottom:`1px solid ${T.border}33`,flexWrap:"wrap",alignItems:"center" }}>
          {!ov.deleted && <>
            {isExpRec && <>
              <Btn onClick={()=>onDelayClick(item.id,"recurring_exp",item)} size="xs" color={T.orange} variant="outline">📅 Move</Btn>
              {ov.partialAmount !== undefined
                ? <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:12,color:T.textSub}}>Paid so far: {currSym()}</span>
                    <input type="number" value={ov.partialAmount||""} placeholder="0" onChange={e=>monthOps.updateRecurringOverride(selectedMonth,item.id,{partialAmount:parseFloat(e.target.value)||0})} style={{background:T.surface,border:`1px solid ${T.orange}`,borderRadius:RADIUS.sm,color:T.orange,padding:"3px 7px",fontSize:12,width:90,outline:"none",fontFamily:FONT,fontWeight:700}}/>
                    <Btn onClick={()=>monthOps.updateRecurringOverride(selectedMonth,item.id,{partialAmount:undefined})} size="xs" variant="ghost" color={T.textSub}>Reset</Btn>
                  </div>
                : <Btn onClick={()=>monthOps.updateRecurringOverride(selectedMonth,item.id,{partialAmount:0})} size="xs" color={T.orange} variant="outline">Pay Partially</Btn>
              }
            </>}
            <Btn onClick={()=>setShowRecDel(true)} size="xs" color={T.red} variant="outline" danger>Delete…</Btn>
          </>}
          {ov.deleted && <Btn onClick={()=>monthOps.updateRecurringOverride(selectedMonth,item.id,{deleted:false})} size="xs" color={T.green} variant="outline">↩ Restore</Btn>}
          <button onClick={()=>onNavigate?.("transactions",{expandId:item.recurringId})} style={{
            background:T.accent+"15", border:`1px solid ${T.accent}30`, color:T.accent, cursor:"pointer",
            fontSize:13, fontFamily:FONT, padding:"6px 12px", borderRadius:RADIUS.sm,
            fontWeight:600, marginLeft:"auto", display:"flex", alignItems:"center", gap:4,
          }}>✏️ Edit rule</button>
        </div>
      </>
    );
  }

  // Note: `category` and `status` here are block-scoped identifiers used
  // only within the render helper above — they were historically written
  // as bare assignments (hitting `window.category` / `window.status`).
  // We declare them here explicitly to avoid the no-undef / no-global-assign
  // lint errors without changing semantics.
  // eslint-disable-next-line no-unused-vars
  let category, status; // eslint-disable-line no-unused-vars
  if (catType === "planner_exp" || catType === "planner_inc") {
    category = "📌 Planner";
    status = "—";
  }
  if (catType === "manual_exp") {
    const manualPartial = item.partialAmount;
    return (
      <div style={{ display:"flex",gap:8,padding:isMobile?"6px 10px":"8px 32px 10px",background:T.card,borderBottom:`1px solid ${T.border}`,flexWrap:"wrap",alignItems:"center" }}>
        {!item.paid && <>
          <Btn onClick={()=>onDelayClick(item.id,"manual_exp",item)} size="xs" color={T.orange} variant="outline">📅 Move</Btn>
          {manualPartial !== undefined
            ? <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontSize:12,color:T.textSub}}>Paid so far: {currSym()}</span>
                <input type="number" value={manualPartial||""} placeholder="0" onChange={e=>{const u={...months,[selectedMonth]:{...months[selectedMonth],expenses:(months[selectedMonth]?.expenses||[]).map(ex=>ex.id===item.id?{...ex,partialAmount:parseFloat(e.target.value)||0}:ex)}};monthOps.setMonthsDirect?.(u);}} style={{background:T.surface,border:`1px solid ${T.orange}`,borderRadius:RADIUS.sm,color:T.orange,padding:"3px 7px",fontSize:12,width:90,outline:"none",fontFamily:FONT,fontWeight:700}}/>
                <Btn onClick={()=>{const u={...months,[selectedMonth]:{...months[selectedMonth],expenses:(months[selectedMonth]?.expenses||[]).map(ex=>ex.id===item.id?{...ex,partialAmount:undefined}:ex)}};monthOps.setMonthsDirect?.(u);}} size="xs" variant="ghost" color={T.textSub}>Reset</Btn>
              </div>
            : <Btn onClick={()=>{const u={...months,[selectedMonth]:{...months[selectedMonth],expenses:(months[selectedMonth]?.expenses||[]).map(ex=>ex.id===item.id?{...ex,partialAmount:0}:ex)}};monthOps.setMonthsDirect?.(u);}} size="xs" color={T.orange} variant="outline">Pay Partially</Btn>
          }
        </>}
        <Btn onClick={()=>onConfirmDelete({label:item.what||item.name||"expense",action:()=>monthOps.deleteExpense(selectedMonth,item.id)})} size="xs" color={T.red} variant="outline" danger>Delete</Btn>
      </div>
    );
  }

  if (catType === "manual_inc") {
    return (
      <IncomeAdjustRow item={item} mData={mData} months={months} selectedMonth={selectedMonth} monthOps={monthOps} T={T}/>
    );
  }
  return null;
}

// ─── Recurring Delete Dialog ──────────────────────────────────────────────────
function RecurringDeleteDialog({ open, itemName, onClose, onDeleteThis, onDeleteAll }) {
  const T = useTheme();
  return (
    <BottomSheet open={open} onClose={onClose} title="🔄 Recurring Transaction" height="auto"
      footer={<Btn onClick={onClose} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn>}>
      <div style={{ display:"flex",flexDirection:"column",gap:10, paddingBottom:8 }}>
        <div style={{ fontSize:13,color:T.textSub }}>"{itemName}" is a recurring transaction.</div>
        <div style={{ background:T.orange+"15",border:`1px solid ${T.orange+"44"}`,borderRadius:RADIUS.md,padding:"10px 14px",fontSize:12,color:T.orange }}>
          ⚠️ What would you like to delete?
        </div>
        <Btn onClick={onDeleteThis} color={T.orange} variant="outline" style={{ width:"100%",justifyContent:"center" }}>Delete this month only</Btn>
        <Btn onClick={onDeleteAll} color={T.red} danger style={{ width:"100%",justifyContent:"center" }}>Delete all occurrences permanently</Btn>
      </div>
    </BottomSheet>
  );
}

// ─── Order Transaction Delete Dialog ─────────────────────────────────────────
function OrderTxDeleteDialog({ open, item, orders, onClose, onDeleteTx, onDelayTx, onNavigateOrder, months, selectedMonth, monthOps }) {
  const T = useTheme();
  if (!item) return null;
  const order = orders?.find(o => o.id === item.orderId);
  const batchLabel = item.batchLabel || item.description || "Payment";
  return (
    <BottomSheet open={open} onClose={onClose} title="📦 Order Transaction" height="auto"
      footer={<Btn onClick={onClose} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn>}>
      <div style={{ display:"flex",flexDirection:"column",gap:8, paddingBottom:4 }}>
        <div style={{ background:T.blue+"12",border:`1px solid ${T.blue+"33"}`,borderRadius:RADIUS.md,padding:"10px 14px",fontSize:12,color:T.textSub }}>
          Belongs to <strong style={{ color:T.text }}>{order?.number || "an order"}</strong> — {batchLabel}
        </div>
        <div style={{ fontSize:12,color:T.textSub }}>What would you like to do?</div>
        <Btn onClick={()=>{ onDelayTx?.(); onClose(); }} color={T.orange} variant="outline" style={{ justifyContent:"flex-start" }}>📅 Move this payment</Btn>
        <Btn onClick={()=>{ onDeleteTx?.(); onClose(); }} color={T.orange} variant="outline" style={{ justifyContent:"flex-start" }}>Delete this payment only</Btn>
        {onNavigateOrder && (
          <Btn onClick={()=>{ onNavigateOrder(); onClose(); }} color={T.blue} variant="outline" style={{ justifyContent:"flex-start" }}>🔗 Open order {order?.number}</Btn>
        )}
      </div>
    </BottomSheet>
  );
}

// ─── Income Adjust Dialog ─────────────────────────────────────────────────────
function IncomeAdjustRow({ item, mData, months, selectedMonth, monthOps, T }) {
  const isMobile = useIsMobile();
  const [adjusting, setAdjusting] = useState(false);
  const [adjValue, setAdjValue] = useState("");
  const overrideKey = `inc_adj_${selectedMonth}_${item.id}`;
  const curAdj = (mData.incomeAdjustments || {})[item.id];
  const hasAdj = curAdj !== undefined;

  function saveAdj() {
    const u = { ...months, [selectedMonth]: { ...months[selectedMonth], incomeAdjustments: { ...(months[selectedMonth]?.incomeAdjustments||{}), [item.id]: parseFloat(adjValue)||0 } } };
    monthOps.setMonthsDirect?.(u);
    setAdjusting(false);
  }
  function restoreAdj() {
    const adj = { ...(months[selectedMonth]?.incomeAdjustments||{}) };
    delete adj[item.id];
    const u = { ...months, [selectedMonth]: { ...months[selectedMonth], incomeAdjustments: adj } };
    monthOps.setMonthsDirect?.(u);
  }
  function deleteInc() {
    const u = { ...months, [selectedMonth]: { ...months[selectedMonth], incomeItems: (months[selectedMonth]?.incomeItems||[]).filter(i=>i.id!==item.id) } };
    monthOps.setMonthsDirect?.(u);
  }

  return (
    <div style={{ display:"flex",gap:8,padding:isMobile?"4px 10px":"6px 32px 10px",background:"transparent",borderBottom:`1px solid ${T.border}33`,flexWrap:"wrap",alignItems:"center" }}>
      {adjusting ? (
        <>
          <span style={{ fontSize:12,color:T.textSub }}>New amount: {currSym()}</span>
          <input autoFocus type="number" value={adjValue} onChange={e=>setAdjValue(e.target.value)} placeholder={String(item.amount||0)}
            style={{ background:T.card,border:`1px solid ${T.blue}`,borderRadius:RADIUS.sm,color:T.text,padding:"3px 8px",fontSize:12,width:110,outline:"none",fontFamily:FONT,fontWeight:700 }}/>
          <Btn onClick={saveAdj} size="xs" color={T.green}>Save</Btn>
          <Btn onClick={()=>setAdjusting(false)} size="xs" variant="ghost" color={T.textSub}>Cancel</Btn>
        </>
      ) : (
        <>
          <Btn onClick={()=>{ setAdjValue(String(curAdj??item.amount??"")); setAdjusting(true); }} size="xs" color={T.blue} variant="outline">
            {hasAdj ? `Adjusted: ${currSym()}${curAdj}` : "Adjust"}
          </Btn>
          {hasAdj && <Btn onClick={restoreAdj} size="xs" color={T.green} variant="outline">↩ Restore</Btn>}
          <Btn onClick={deleteInc} size="xs" color={T.red} variant="outline" danger>Delete</Btn>
        </>
      )}
    </div>
  );
}

// ─── ActionRow ────────────────────────────────────────────────────────────────
function TableRow({ item, catType, isExpense, mData, months, selectedMonth, monthOps, prevMData, T, onDelayClick, orders, onNavigate, recurringExpenseOps, recurringIncomeOps, highlightId, setReminderTarget, can, onConfirmDelete, onMarkPaid }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // Determine if this row matches the highlight
  const rowId = item.id || item.recurringId || item.mktId;
  const isHighlighted = highlightId && rowId && (rowId === highlightId);

  function getDisplay() {
    let name = item.what || item.name || "—";
    let amount = item.amount || 0;
    let date = item.date || null;
    let status = "pending";
    let prevAmount = 0;
    let delayedDate = null;
    let amountPaid = 0; // track how much was paid for display
    let paidAt = null;

    if (catType === "delivery") {
      const ov = (mData.deliveryOverrides||{})[item.id]||{};
      if (ov.partialAmount !== undefined) { amountPaid = ov.partialAmount; amount = Math.max(0, (item.amount||0) - amountPaid); }
      if (ov.paid) { status = "paid"; paidAt = ov.paidAt || null; }
      if (ov.deleted) status = "deleted";
      if (ov.date) date = ov.date;
      if (ov.delayedDate) { date = ov.delayedDate; delayedDate = ov.delayedDate; }
      else if (ov.delayedTo) delayedDate = ov.delayedTo; // backward compat
    }
    if (catType === "orders") {
      const isPaid = (mData.orderExpPaid||{})[item.id]||false;
      const partial = (mData.orderExpPartial||{})[item.id];
      if (partial !== undefined) { amountPaid = partial; amount = Math.max(0, (item.amount||0) - amountPaid); }
      if (isPaid) { status = "paid"; paidAt = (mData.orderExpPaidAt||{})[item.id] || null; }
      const delayed = (mData.orderExpDelayed||{})[item.id];
      if (delayed && delayed.newDate) { date = delayed.newDate; delayedDate = delayed.newDate; }
      else if (typeof delayed === "string") delayedDate = delayed; // backward compat
    }
    if (catType === "recurring_exp" || catType === "recurring_inc") {
      const ov = (mData.recurringOverrides||{})[item.id]||{};
      if (ov.amount !== undefined) amount = ov.amount;
      if (ov.partialAmount !== undefined) { amountPaid = ov.partialAmount; amount = Math.max(0, (ov.amount !== undefined ? ov.amount : (item.amount||0)) - amountPaid); }
      if (ov.paid) { status = "paid"; paidAt = ov.paidAt || null; }
      if (ov.deleted) status = "deleted";
      if (ov.delayedDate) { date = ov.delayedDate; delayedDate = ov.delayedDate; }
      else if (ov.delayedTo) delayedDate = ov.delayedTo; // backward compat
    }
    if (catType === "payouts") {
      name = item.mktName || "Payout";
      amount = item.payout || 0;
      date = item.arrivalDate;
      // Past payouts are already included in the initial balance — mark as received
      const payDate = date ? (() => { const p=date.split("."); return p.length===3?new Date(+p[2],+p[1]-1,+p[0]):null; })() : null;
      status = payDate && payDate < new Date() ? "received" : "projected";
    }
    if (catType === "manual_exp") {
      if (item.paid) { status = "paid"; paidAt = item.paidAt || null; }
      if (item.partialAmount !== undefined) { amountPaid = item.partialAmount; amount = Math.max(0, (item.amount||0) - amountPaid); }
    }

    // Past income items (not expenses) that aren't already marked paid — show as "received"
    // These are already included in the user's initial balance.
    if (!isExpense && status !== "paid" && status !== "deleted" && status !== "received" && date) {
      const parts = date.split(".");
      if (parts.length === 3) {
        const d = new Date(+parts[2], +parts[1]-1, +parts[0]);
        if (d < new Date()) status = "received";
      }
    }
    return { name, amount, date, status, delayedDate, amountPaid, paidAt };
  }

  const { name, amount, date, status, delayedDate, amountPaid, paidAt } = getDisplay();
  const dateFormat = getDateFormat(months?._appSettings);
  const paidAtTitle = paidAt ? `Paid at ${formatDateTime(paidAt, dateFormat)}` : null;

  const statusStyles = {
    paid:      { color: "#34c759", label: "✓ Paid" },
    received:  { color: "#34c759", label: "✓ In balance" },
    delayed:   { color: "#ff9500", label: "📅 Moved" },
    projected: { color: "#86868b", label: "Projected" },
    pending:   { color: "#86868b", label: "Pending" },
  };
  const ss = statusStyles[status] || statusStyles.pending;

  const amtColor = isExpense ? T.red || "#ff3b30" : T.green || "#34c759";

  // Payouts are expandable to show per-product breakdown
  const canExpand = catType === "payouts" ? (item.products||[]).length > 0 : catType !== "payouts";

  // Per-product breakdown for payout rows
  const products = catType === "payouts" ? (item.products||[]).filter(p => p.units > 0) : [];

  return (
    <>
      <div
        id={`ch-txrow-${rowId}`}
        onClick={() => {}}
        style={{
          display:"grid", gridTemplateColumns: isMobile ? "1fr auto" : "1fr 110px 210px",
          padding:"9px 16px", borderBottom:`1px solid ${T.border}`,
          borderRadius: isHighlighted ? RADIUS.md : 0,
          cursor: "default",
          opacity: status==="paid"||status==="received" ? 0.4 : 1,
          background: isHighlighted ? T.red+"18" : open ? T.surface : "transparent",
          boxShadow: isHighlighted ? `inset 0 0 0 2px ${T.red}, 0 0 18px ${T.red}44` : "none",
          transition:"background 0.1s, box-shadow 0.4s",
          animation: isHighlighted ? "chHighlightPulse 1s ease-in-out 3" : "none",
          position:"relative",
          zIndex: isHighlighted ? 2 : "auto",
        }}
        onMouseEnter={e=>{if(canExpand&&!isHighlighted)e.currentTarget.style.background=T.surface;}}
        onMouseLeave={e=>{e.currentTarget.style.background=isHighlighted?T.red+"18":open?T.surface:"transparent";}}
      >
        {/* Name + date */}
        <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
          <span style={{ fontSize:12, color:T.textSub, width:76, flexShrink:0, fontVariantNumeric:"tabular-nums" }}>{delayedDate || date || "—"}</span>
          <span title={name} onClick={e=>{e.stopPropagation(); if(name.length>25){const el=e.currentTarget; el.style.whiteSpace=el.style.whiteSpace==="normal"?"nowrap":"normal"; el.style.overflow=el.style.whiteSpace==="nowrap"?"hidden":"visible";}}} style={{ fontSize:14, color: status==="paid"||status==="delayed"||status==="received" ? T.textSub : T.text, textDecoration: status==="paid"||status==="received" ? "line-through" : "none", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight: status==="paid"||status==="received" ? 400 : 500, cursor:"pointer" }}>{name}</span>
          {catType === "payouts" && products.length > 0 && (
            <span style={{ fontSize:12, color:T.blue, background:T.blue+"18", padding:"1px 7px", borderRadius:RADIUS.pill, flexShrink:0 }}>
              {open ? "▲" : "▼"} {products.length} product{products.length !== 1 ? "s" : ""}
            </span>
          )}
          {delayedDate && <span style={{ fontSize:12, color:T.orange, background:T.orange+"20", padding:"1px 7px", borderRadius:RADIUS.pill, flexShrink:0 }}>📅 Moved{item.date && delayedDate !== item.date ? ` from ${item.date}` : ""}</span>}
        </div>
        {/* Amount */}
        <span style={{ fontSize:14, fontWeight:700, color:amtColor, textAlign:"right",
          fontVariantNumeric:"tabular-nums", flexShrink:0, minWidth:80, display:"flex", alignItems:"center", justifyContent:"flex-end", gap:4 }}>
          {isExpense?"-":"+"}{fmtFull(amount)}
          {amountPaid > 0 && <span title={`${currSym()}${amountPaid.toLocaleString()} paid so far`} style={{ width:7, height:7, borderRadius:"50%", background:T.orange, flexShrink:0, cursor:"help" }}/>}
        </span>
        {/* Actions — desktop: inline row. Mobile: compact wrap */}
        <div style={{ display:"flex", gap: isMobile ? 3 : 5, alignItems:"center", justifyContent:"flex-end", flexShrink:0, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          {/* Mark Paid button - visible for unpaid expense rows (edit permission required) */}
          {isExpense && status !== "paid" && catType !== "payouts" && catType !== "planner_exp" && (!can || can("calendar","edit")) && (
            <button onClick={e => {
              e.stopPropagation();
              const paidAmt = amount; // amount from getDisplay() is already the remaining amount
              if (catType === "manual_exp") onMarkPaid(()=>monthOps.toggleExpensePaid(selectedMonth, item.id), paidAmt, name);
              else if (catType === "delivery") onMarkPaid(()=>monthOps.updateDeliveryOverride(selectedMonth, item.id, { paid: true }), paidAmt, name);
              else if (catType === "orders") onMarkPaid(()=>monthOps.toggleOrderExpPaid(selectedMonth, item.id), paidAmt, name);
              else if (catType === "recurring_exp") onMarkPaid(()=>monthOps.updateRecurringOverride(selectedMonth, item.id, { paid: true }), paidAmt, name);
            }} style={{ background:T.green+"18", border:`1px solid ${T.green+"55"}`, color:T.green, borderRadius:RADIUS.pill, padding:"3px 9px", fontSize:12, fontWeight:700, fontFamily:FONT, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap" }}>
              ✓ Mark Paid
            </button>
          )}
          {status === "paid" && catType !== "payouts" && (!can || can("calendar","edit")) && (
            <button
              title={paidAtTitle || undefined}
              onClick={e => {
              e.stopPropagation();
              if (catType === "manual_exp") monthOps.toggleExpensePaid(selectedMonth, item.id);
              else if (catType === "delivery") monthOps.updateDeliveryOverride(selectedMonth, item.id, { paid: false });
              else if (catType === "orders") monthOps.toggleOrderExpPaid(selectedMonth, item.id);
              else if (catType === "recurring_exp") monthOps.updateRecurringOverride(selectedMonth, item.id, { paid: false });
            }} style={{ background:T.green+"18", border:`1px solid ${T.green}`, color:T.green, borderRadius:RADIUS.pill, padding:"3px 9px", fontSize:12, fontWeight:700, fontFamily:FONT, cursor:"pointer", flexShrink:0 }}>
              ✓ Paid
            </button>
          )}
          {/* Reminder bell — on all non-payout rows */}
          {catType !== "payouts" && (
            <button
              onClick={e => { e.stopPropagation(); setReminderTarget({ kind:"transaction", id:rowId, label:name }); }}
              title="Add reminder"
              style={{ background:"transparent", border:`1px solid ${T.border}`, color:T.textMuted, borderRadius:RADIUS.pill, padding:"3px 7px", fontSize:12, fontFamily:FONT, cursor:"pointer", flexShrink:0 }}>
              🔔
            </button>
          )}
          {/* Attachment icon — visible on every editable transaction row so
              the invoice/receipt slot is obvious without expanding. Maps
              catType → the entity_type used by the attachments table.
              Order-sourced rows fall back to the parent order's invoice
              PDF so users don't have to attach the same file per term. */}
          {(catType === "manual_exp" || catType === "manual_inc" ||
            catType === "delivery" || catType === "orders" ||
            catType === "recurring_exp" || catType === "recurring_inc") && (
            <span onClick={e => e.stopPropagation()} style={{ flexShrink:0 }}>
              <AttachmentButton
                entityType={
                  catType === "manual_exp" ? "expense"
                  : catType === "manual_inc" ? "income_item"
                  : catType === "delivery" ? "delivery_override"
                  : catType === "orders" ? "order_expense"
                  : "recurring"
                }
                entityId={rowId}
                size={15}
                fallback={catType === "orders" && item.orderId
                  ? { entityType: "order", entityId: item.orderId }
                  : null}
              />
            </span>
          )}
          {/* Payout expand — keep for payouts only */}
          {catType === "payouts" && products.length > 0 && (
            <button onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
              style={{ background:"transparent", border:`1px solid ${T.border}`, color:T.textMuted, borderRadius:RADIUS.pill, padding:"3px 8px", fontSize:12, fontFamily:FONT, cursor:"pointer", flexShrink:0 }}>
              {open ? "▲" : "▼"} Details
            </button>
          )}
          {/* Expand arrow — toggles action row below (always visible, full opacity) */}
          {canExpand && catType !== "payouts" && (
            <button onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
              title={open ? "Collapse" : "Expand actions"}
              style={{ background:open?T.orange+"18":"transparent",
                border:`1px solid ${open?T.orange:T.border}`,
                color:open?T.orange:T.textSub,
                borderRadius:RADIUS.pill, padding:"4px 10px", fontSize:15,
                fontFamily:FONT, cursor:"pointer", lineHeight:1, flexShrink:0,
                opacity:1 }}>
              {open ? "▴" : "▾"}
            </button>
          )}
        </div>
      </div>

      {open && catType === "payouts" && products.length > 0 && (
        <div style={{ background:"transparent", borderBottom:`1px solid ${T.border}33`, padding:"8px 16px 12px 16px" }}>
          <div style={{ fontSize:12, fontWeight:700, color:T.textSub, letterSpacing:0.5, textTransform:"uppercase", marginBottom:8 }}>Product Breakdown</div>
          {products.map(prod => (
            <div key={prod.prodId} style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr auto" : "1fr 110px 210px", padding:"5px 0", borderBottom:"1px solid "+T.border+"22", alignItems:"center" }}>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ fontSize:13, color:T.text, fontWeight: prod._isGroup ? 700 : 400 }}>{prod.prodName}</span>
                {prod._isGroup && (
                  <span style={{ fontSize:10, color:T.accent, fontWeight:700, padding:"1px 6px", borderRadius:RADIUS.pill, background:T.accent+"15", border:`1px solid ${T.accent}40` }}>
                    {prod._childCount} {prod._childCount === 1 ? "product" : "products"}
                  </span>
                )}
                <span style={{ fontSize:12, color:T.textMuted, background:T.surfaceHi, padding:"1px 8px", borderRadius:RADIUS.pill }}>{prod.units} units</span>
              </div>
              <span style={{ fontSize:13, fontWeight:700, color:T.green, fontVariantNumeric:"tabular-nums", textAlign:"right" }}>+{fmtFull(prod.payout)}</span>
              <span style={{ fontSize:12, color:T.textSub, textAlign:"right" }}>{prod.units > 0 ? `${currSym()}${(prod.payout/prod.units).toFixed(2)}/unit` : ""}</span>
            </div>
          ))}
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr auto" : "1fr 110px 210px", marginTop:8, paddingTop:6, borderTop:`1px solid ${T.border}` }}>
            <span style={{ fontSize:12, fontWeight:600, color:T.textSub }}>Total · {products.reduce((s,p)=>s+p.units,0)} units</span>
            <span style={{ fontSize:13, fontWeight:800, color:T.green, textAlign:"right" }}>+{fmtFull(products.reduce((s,p)=>s+p.payout,0))}</span>
          </div>
        </div>
      )}

      {/* Expanded action row for non-payout types */}
      {open && catType !== "payouts" && (
        <ActionRow catType={catType} item={item} mData={mData} months={months}
          selectedMonth={selectedMonth} monthOps={monthOps}
          onDelayClick={onDelayClick} T={T} orders={orders}
          onNavigate={onNavigate}
          recurringExpenseOps={recurringExpenseOps}
          recurringIncomeOps={recurringIncomeOps}
          onConfirmDelete={onConfirmDelete}
          onMarkPaid={onMarkPaid}
        />
      )}

    </>
  );
}

// ─── Main MonthlyView ─────────────────────────────────────────────────────────
export default function CashCalendar({
  marketplaces, months, orders, recurringIncome, recurringExpenses,
  partners, deliveryCreditLimit, visibleYears, fbmSettings = [],
  decisions, navContext, availableToday, setAvailableToday,
  monthOps, recurringIncomeOps, recurringExpenseOps, setPartners, onNavigate, onAddDecision,
  can, isOwner,
  inventoryPools = [],
  modifiers = [],
  account,
}) {
  const T = useTheme();
  const isMobile = useIsMobile();
  const TODAY = useMemo(() => new Date(), []);
  const currentKey = mkKey(TODAY.getFullYear(), TODAY.getMonth() + 1);

  // ── Marketplace filter state — independent per page (matches Dashboard
  //    pattern). When the user picks a single marketplace, only that
  //    marketplace's payouts/income/expenses contribute. Multi-select sums
  //    the selected ones; "all" sums every marketplace. ──
  const [selMkts, setSelMkts] = useState(() => {
    try { const v = LS.get?.(LS.CC_SELMKTS, "all"); return v || "all"; } catch { return "all"; }
  });
  useEffect(() => {
    try { LS.set?.(LS.CC_SELMKTS, selMkts); } catch {}
  }, [selMkts]);
  const isMktSelected = (mktId) => {
    if (selMkts === "all" || !Array.isArray(selMkts) || !selMkts.length) return true;
    return selMkts.includes(mktId);
  };
  function toggleSelMkt(id) {
    if (selMkts === "all" || !Array.isArray(selMkts)) { setSelMkts([id]); return; }
    if (selMkts.includes(id)) {
      const next = selMkts.filter(x => x !== id);
      setSelMkts(next.length ? next : "all");
    } else {
      setSelMkts([...selMkts, id]);
    }
  }

  // ── Timeframe state: single month, multi-month, year, week, or custom range ──
  const [selectedMonths, setSelectedMonths] = useState([currentKey]);
  const selectedMonth = selectedMonths[0] || currentKey;
  const [tfMode, setTfMode] = useState("month"); // "month"|"multi"|"year"|"custom"
  const [selectedYear, setSelectedYear] = useState(TODAY.getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(null); // 1-4 or null
  const [confirmMarkAllPaid, setConfirmMarkAllPaid] = useState(false);
  const [customFrom, setCustomFrom] = useState(""); // "DD.MM.YYYY"
  const [customTo, setCustomTo]   = useState(""); // "DD.MM.YYYY"

  // ── Highlight state — driven by navContext from Dashboard drill-through ──
  const [highlightId, setHighlightId] = useState(null);
  const highlightRef = useRef(null);
  const prevNavTs = useRef(null);

  useEffect(() => {
    if (!navContext?.month) return;
    const ts = navContext.ts || navContext.month;
    if (ts === prevNavTs.current) return;
    prevNavTs.current = ts;

    const targetMonth = navContext.month;
    const [y] = targetMonth.split("-").map(Number);
    setSelectedMonths([targetMonth]);
    setSelectedYear(y);

    if (navContext.customDay) {
      // Day view: use custom range for single day
      setTfMode("custom");
      setCustomFrom(navContext.customDay);
      setCustomTo(navContext.customDay);
      setSelectedWeek(null);
    } else if (navContext.week) {
      // Week view: select month and specific week
      // Force tfMode to "month" first, then set the week
      setTfMode("month");
      // Use setTimeout to ensure tfMode state settles before week is applied
      const pendingWeek = navContext.week;
      setSelectedWeek(null); // reset first
      setTimeout(() => setSelectedWeek(pendingWeek), 0);
    } else {
      // Month view: just select the month
      setTfMode("month");
      setSelectedWeek(null);
    }

    // Set highlight — clear after 4s
    if (navContext.highlightId) {
      setHighlightId(navContext.highlightId);
      setTimeout(() => setHighlightId(null), 4000);
    }
  }, [navContext]);

  // Scroll highlighted row into view after render
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`ch-txrow-${highlightId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [highlightId]);

  // ── UI state ──
  const [showNewAddModal, setShowNewAddModal] = useState(false);
  // Defaults handed to the AddTransactionModal — set by whichever button
  // the user picked in the popover (Income, Partner, Credit Line, etc).
  // Empty {} = the generic "Add Transaction" entry → modal uses its own
  // defaults (expense / general).
  const [addModalDefaults, setAddModalDefaults] = useState({});
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  function pickAddOption({ type = "expense", category = "general" } = {}) {
    setAddModalDefaults({ type, category });
    setAddPopoverOpen(false);
    setShowNewAddModal(true);
  }
  // VAT preset — quarterly EU template, ported verbatim from Recurring.jsx
  // so the button keeps doing exactly what it used to do. Pre-generates
  // the id so we can scroll+highlight the new row immediately.
  function addVatPreset() {
    const newId = uid();
    recurringExpenseOps.add({
      id:           newId,
      name:         "VAT Payment",
      amount:       0,
      type:         "calendar",
      isIncome:     false,
      activeMonths: [1, 4, 7, 10],
      dayOfMonth:   1,
      startDate:    "",
      activeYears:  [],
      intervalDays: 30,
      vatPreset:    true,
    });
    setAddPopoverOpen(false);
    setHighlightId(newId);
    setTimeout(() => setHighlightId(null), 4000);
  }
  const [delayTarget, setDelayTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTypes, setFilterTypes] = useState(["all"]);
  const [barAnimKey, setBarAnimKey] = useState(0);
  const [comparisonView, setComparisonView] = useState(false);
  // Re-trigger bar animation on any setting change
  useEffect(() => { setBarAnimKey(k => k + 1); }, [selectedMonths, selectedWeek, tfMode, selectedYear, filterTypes]);
  const [filterDropOpen, setFilterDropOpen] = useState(false);
  const filterType = filterTypes.includes("all") ? "all" : filterTypes; // backward compat
  const toggleFilter = (v) => {
    if (v === "all") { setFilterTypes(["all"]); return; }
    setFilterTypes(prev => {
      const without = prev.filter(f => f !== "all");
      const next = without.includes(v) ? without.filter(f => f !== v) : [...without, v];
      return next.length === 0 ? ["all"] : next;
    });
  };
  const [exportWhat, setExportWhat] = useState("both"); // "both"|"expenses"|"income"
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null); // {title, kind, id}
  const [calConfirmDelete, setCalConfirmDelete] = useState(null); // {label, action}
  const [reduceBalancePrompt, setReduceBalancePrompt] = useState(null); // {amount, label}

  // Helper: show reduce-balance prompt after marking an expense as paid
  function markPaidWithPrompt(markAction, amount, label) {
    markAction(); // do the mark-paid first
    if (setAvailableToday && amount > 0) {
      setReduceBalancePrompt({ amount: Math.round(amount * 100) / 100, label: label || "Expense" });
    }
  }

  const deliveryExpenses = useMemo(() => computeDeliveryExpenses(months, marketplaces, deliveryCreditLimit, fbmSettings), [months, marketplaces, deliveryCreditLimit, fbmSettings]);
  const orderExpenses = useMemo(() => generateOrderExpenses(orders), [orders]);
  const allRecurring = useMemo(() => [
    ...recurringExpenses.map(r => ({ ...r, isIncome: false })),
    ...recurringIncome.map(r => ({ ...r, isIncome: true })),
    ...partners.map(p => ({ ...p, isIncome: false, type: p.type || "calendar" })),
  ], [recurringExpenses, recurringIncome, partners]);
  const recurringItems = useMemo(() => generateRecurringItems(allRecurring, visibleYears), [allRecurring, visibleYears]);
  const adSpendItems = useMemo(() => generateAdSpendExpenses(marketplaces, months, visibleYears), [marketplaces, months, visibleYears]);
  // Merge ad spend into recurring items so they appear in expense rows
  const mergedRecurringItems = useMemo(() => {
    const merged = { ...recurringItems };
    for (const [k, v] of Object.entries(adSpendItems)) {
      merged[k] = [...(merged[k] || []), ...v];
    }
    return merged;
  }, [recurringItems, adSpendItems]);

  // Marketplace filter — apply selMkts to the brand-wide breakdown so the
  // visible income lines and totals reflect the user's selection. The helper
  // returns one row per marketplace; we just filter the rows.
  const payoutBreakdown = useMemo(() => {
    const modMkts = applyModifiers(marketplaces, modifiers, selectedMonth);
    const all = getMonthlyPayoutBreakdown(selectedMonth, marketplaces, months, modMkts);
    return all.filter(row => isMktSelected(row.mktId));
  }, [selectedMonth, marketplaces, months, selMkts]);
  // Planner-linked cashflow items for the selected months
  const plannerItems = useMemo(() => {
    if (!decisions || !decisions.length) return {};
    const recurring = generateDecisionRecurringItems(decisions, visibleYears);
    const oneTime = generateDecisionOneTimeItems(decisions, visibleYears);
    const merged = { ...recurring };
    for (const [k, v] of Object.entries(oneTime)) {
      merged[k] = [...(merged[k] || []), ...v];
    }
    return merged;
  }, [decisions, visibleYears]);

  const prevMonthKey = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    return m === 1 ? mkKey(y - 1, 12) : mkKey(y, m - 1);
  }, [selectedMonth]);

  const [yr, mo] = selectedMonth.split("-").map(Number);
  const mData = months[selectedMonth] || {};
  const prevMData = months[prevMonthKey] || {};

  // ── Date-in-range helper for week/custom filtering ──
  const dateInRange = useCallback((dateStr) => {
    if (!dateStr) return true; // no date = include
    // Parse DD.MM.YYYY
    const parts = dateStr.split(".");
    if (parts.length !== 3) return true;
    const d = new Date(+parts[2], +parts[1]-1, +parts[0]);

    if (selectedWeek && selectedMonths.length === 1) {
      const [y, m] = selectedMonth.split("-").map(Number);
      const weekStart = (selectedWeek - 1) * 7 + 1;
      const weekEnd = selectedWeek * 7;
      const dayOfMonth = d.getDate();
      return d.getFullYear() === y && d.getMonth()+1 === m && dayOfMonth >= weekStart && dayOfMonth <= weekEnd;
    }
    if (tfMode === "custom" && customFrom && customTo) {
      const fp = customFrom.split("."); const tp = customTo.split(".");
      if (fp.length === 3 && tp.length === 3) {
        const from = new Date(+fp[2], +fp[1]-1, +fp[0]);
        const to   = new Date(+tp[2], +tp[1]-1, +tp[0]);
        return d >= from && d <= to;
      }
    }
    return true;
  }, [selectedWeek, selectedMonth, selectedMonths, tfMode, customFrom, customTo]);

  // Build flat item lists — aggregated across ALL selectedMonths
  const expenseRows = useMemo(() => {
    // Marketplace filter applies to expense rows: items tagged with a
    // marketplaceId are skipped if that marketplace isn't currently selected.
    // Untagged items (recurring expenses, manual transactions, generic
    // delivery) always pass through — they're brand-wide by design.
    const rows = [];
    for (const key of selectedMonths) {
      const kData = months[key] || {};
      (deliveryExpenses[key] || []).forEach(e => {
        const ov = (kData.deliveryOverrides||{})[e.id]||{};
        if (ov.deleted) return;
        if (!dateInRange(ov.date || e.date)) return;
        if (e.marketplaceId && !isMktSelected(e.marketplaceId)) return;
        rows.push({ ...e, catType:"delivery", _month:key });
      });
      (orderExpenses[key] || []).forEach(e => {
        if (!dateInRange(e.date)) return;
        // Order expenses inherit the marketplace from their parent order;
        // an order with marketplaceIds[] is filtered if NONE intersect.
        if (Array.isArray(e.marketplaceIds) && e.marketplaceIds.length > 0
            && !e.marketplaceIds.some(isMktSelected)) return;
        rows.push({ ...e, catType:"orders", _month:key });
      });
      (mergedRecurringItems[key] || []).filter(r => !r.isIncome).forEach(r => {
        const ov = (kData.recurringOverrides||{})[r.id||r.recurringId]||{};
        if (ov.deleted) return;
        if (!dateInRange(r.date)) return;
        if (r.marketplaceId && !isMktSelected(r.marketplaceId)) return;
        rows.push({ ...r, catType:"recurring_exp", _month:key });
      });
      (kData.expenses || []).forEach(e => {
        if (!dateInRange(e.date)) return;
        if (e.marketplaceId && !isMktSelected(e.marketplaceId)) return;
        rows.push({ ...e, catType:"manual_exp", _month:key });
      });
      for (const pi of (plannerItems[key]||[])) {
        if (!pi.amount || pi.amount === 0 || pi.isIncome) continue;
        if (pi.marketplaceId && !isMktSelected(pi.marketplaceId)) continue;
        rows.push({ ...pi, catType:"planner_exp", _month:key,
          what:(pi.what||"Planner"), isPlanner:true });
      }
    }
    return rows;
  }, [deliveryExpenses, orderExpenses, mergedRecurringItems, plannerItems, months, selectedMonths, dateInRange, selMkts]);

  const incomeRows = useMemo(() => {
    const rows = [];
    for (const key of selectedMonths) {
      const kData = months[key] || {};

      // Sum the TOTAL API-sourced payout amount per marketplace for this
      // month. We subtract this from the projected payout so the user sees:
      //   - Real API payouts (as individual line items below)
      //   - REMAINING projected income (projection minus what API already reported)
      // This handles partial months: e.g., 2 API payouts totaling $9,500
      // out of a projected $15,000 → the projected row shows $5,500 remaining.
      const apiPayoutSumByMkt = {};
      for (const e of kData.incomeItems || []) {
        if (e._apiSourced && e.marketplaceId) {
          apiPayoutSumByMkt[e.marketplaceId] = (apiPayoutSumByMkt[e.marketplaceId] || 0) + (Number(e.amount) || 0);
        }
      }

      // Target-projected payouts — reduced by any API payouts already received
      // for that marketplace. If the API covers the full projected amount (or
      // more), the projected row is suppressed entirely.
      getMonthlyPayoutBreakdown(key, marketplaces, months, applyModifiers(marketplaces, modifiers, key))
        .filter(m => m.units > 0 && isMktSelected(m.mktId))
        .forEach(m => {
          const apiAmount = apiPayoutSumByMkt[m.mktId] || 0;
          const remaining = m.payout - apiAmount;
          if (remaining > 0) {
            rows.push({ ...m, payout: remaining, catType:"payouts", _month:key,
              what: apiAmount > 0 ? `${m.mktName} (remaining projected)` : undefined });
          }
          // If remaining <= 0, the API payouts fully cover (or exceed) the
          // projection — don't show a projected row at all.
        });

      // Recurring income
      (mergedRecurringItems[key] || []).filter(r => r.isIncome).forEach(r => {
        if (!dateInRange(r.date)) return;
        if (r.marketplaceId && !isMktSelected(r.marketplaceId)) return;
        rows.push({ ...r, catType:"recurring_inc", _month:key });
      });
      // Manual + API income items. API-sourced payouts carry _apiSourced=true
      // and replace the target-projected row for their marketplace (above).
      (kData.incomeItems || []).forEach(e => {
        if (!dateInRange(e.date)) return;
        if (e.marketplaceId && !isMktSelected(e.marketplaceId)) return;
        rows.push({ ...e, catType: e._apiSourced ? "api_income" : "manual_inc", _month:key });
      });
      for (const pi of (plannerItems[key]||[])) {
        if (!pi.amount || pi.amount === 0) continue;
        if (pi.marketplaceId && !isMktSelected(pi.marketplaceId)) continue;
        rows.push({ ...pi, catType: pi.isIncome ? "planner_inc" : "planner_exp", _month:key,
          what: (pi.what||"Planner"), isPlanner:true });
      }
    }
    return rows;
  }, [payoutBreakdown, mergedRecurringItems, plannerItems, months, marketplaces, selectedMonths, dateInRange, selMkts]);

  // Filter rows by search and type
  function filterRows(rows, isExpense) {
    return rows.filter(row => {
      const name = (row.mktName || row.what || row.name || row.description || "").toLowerCase();
      const matchSearch = !searchQuery || name.includes(searchQuery.toLowerCase());
      let matchFilter = true;
      const ft = Array.isArray(filterType) ? filterType : [filterType];
      if (!ft.includes("all")) {
        matchFilter = false;
        for (const f of ft) {
          if (f === "unpaid") {
            if (row.catType === "manual_exp" && !row.paid) matchFilter = true;
            else if (row.catType === "delivery" && !((mData.deliveryOverrides||{})[row.id]?.paid)) matchFilter = true;
            else if (row.catType === "orders" && !((mData.orderExpPaid||{})[row.id])) matchFilter = true;
            else if (row.catType === "recurring_exp" && !((mData.recurringOverrides||{})[row.id||row.recurringId]?.paid)) matchFilter = true;
          } else if (f === "paid") {
            if (row.catType === "manual_exp" && !!row.paid) matchFilter = true;
            else if (row.catType === "delivery" && !!((mData.deliveryOverrides||{})[row.id]?.paid)) matchFilter = true;
            else if (row.catType === "orders" && !!((mData.orderExpPaid||{})[row.id])) matchFilter = true;
            else if (row.catType === "recurring_exp" && !!((mData.recurringOverrides||{})[row.id||row.recurringId]?.paid)) matchFilter = true;
          } else if (f === "manual" && (row.catType === "manual_exp" || row.catType === "manual_inc")) matchFilter = true;
          else if (f === "planner" && (row.catType === "planner_exp" || row.catType === "planner_inc")) matchFilter = true;
          else if (f === "delivery" && row.catType === "delivery") matchFilter = true;
          else if (f === "orders" && row.catType === "orders") matchFilter = true;
          else if (f === "recurring" && (row.catType === "recurring_exp" || row.catType === "recurring_inc")) matchFilter = true;
          else if (f === "payouts" && row.catType === "payouts") matchFilter = true;
        }
      }
      return matchSearch && matchFilter;
    });
  }

  const filteredExpenseRows = useMemo(() => filterRows(expenseRows, true), [expenseRows, searchQuery, filterTypes, mData]);
  const filteredIncomeRows = useMemo(() => filterRows(incomeRows, false), [incomeRows, searchQuery, filterTypes, mData]);

  function getMonthTotals(key) {
    const d = months[key] || {};
    const delT = (deliveryExpenses[key] || []).filter(e => !((d.deliveryOverrides||{})[e.id]?.paid||(d.deliveryOverrides||{})[e.id]?.deleted)).reduce((s,e)=>s+e.amount,0);
    const ordT = (orderExpenses[key] || []).filter(e => !(d.orderExpPaid||{})[e.id]).reduce((s,e)=>s+e.amount,0);
    const manT = (d.expenses||[]).filter(e=>!e.paid).reduce((s,e)=>s+e.amount,0);
    const recExpT = (mergedRecurringItems[key]||[]).filter(r=>!r.isIncome).reduce((s,r)=>{const ov=(d.recurringOverrides||{})[r.id||r.recurringId]||{};return s+(!ov.deleted&&!ov.paid?(ov.amount!==undefined?ov.amount:r.amount):0);},0);
    const recIncT = (mergedRecurringItems[key]||[]).filter(r=>r.isIncome).reduce((s,r)=>{const ov=(d.recurringOverrides||{})[r.id||r.recurringId]||{};return s+(!ov.deleted?(ov.amount!==undefined?ov.amount:r.amount):0);},0);
    // Manual + API income items — filtered by marketplace selection
    const manIncT = (d.incomeItems||[]).filter(e => !e.marketplaceId || isMktSelected(e.marketplaceId)).reduce((s,e)=>s+(Number(e.amount)||0),0);
    // Target-projected payouts — reduced by API payouts per marketplace,
    // filtered by marketplace selection.
    const apiSumByMkt = {};
    for (const e of d.incomeItems || []) {
      if (e._apiSourced && e.marketplaceId && isMktSelected(e.marketplaceId)) {
        apiSumByMkt[e.marketplaceId] = (apiSumByMkt[e.marketplaceId] || 0) + (Number(e.amount) || 0);
      }
    }
    const payT = getMonthlyPayoutBreakdown(key,marketplaces,months, applyModifiers(marketplaces, modifiers, key))
      .filter(m => isMktSelected(m.mktId))
      .reduce((s,m) => s + Math.max(0, m.payout - (apiSumByMkt[m.mktId] || 0)), 0);
    return { exp: delT+ordT+manT+recExpT, inc: recIncT+manIncT+payT };
  }

  const { exp, inc } = getMonthTotals(selectedMonth);
  const paidT = useMemo(() => {
    let total = 0;
    // Manual expenses marked paid
    for (const e of (mData.expenses||[])) { if (e.paid) total += e.amount||0; }
    // Delivery expenses marked paid
    for (const e of (deliveryExpenses[selectedMonth]||[])) {
      const ov = (mData.deliveryOverrides||{})[e.id]||{};
      if (ov.paid) total += ov.partialAmount !== undefined ? ov.partialAmount : e.amount||0;
    }
    // Order payments marked paid
    for (const e of (orderExpenses[selectedMonth]||[])) {
      if ((mData.orderExpPaid||{})[e.id]) total += (mData.orderExpPartial||{})[e.id] ?? (e.amount||0);
    }
    // Recurring expenses marked paid
    for (const r of (mergedRecurringItems[selectedMonth]||[])) {
      if (r.isIncome) continue;
      const ov = (mData.recurringOverrides||{})[r.id||r.recurringId]||{};
      if (ov.paid) total += ov.amount !== undefined ? ov.amount : r.amount||0;
    }
    return total;
  }, [mData, deliveryExpenses, orderExpenses, mergedRecurringItems, selectedMonth]);

  // Totals — use filtered rows so week/date-range filter is respected
  function sumExpRows(rows) {
    return rows.reduce((s, r) => {
      if (r.catType === "delivery") { const ov=(mData.deliveryOverrides||{})[r.id]||{}; if(ov.deleted||ov.paid) return s; return s+(ov.partialAmount!==undefined?ov.partialAmount:r.amount); }
      if (r.catType === "orders") { const isPaid=(mData.orderExpPaid||{})[r.id]; if(isPaid) return s; const p=(mData.orderExpPartial||{})[r.id]; return s+(p!==undefined?p:r.amount); }
      if (r.catType === "recurring_exp") { const ov=(mData.recurringOverrides||{})[r.id||r.recurringId]||{}; if(ov.deleted||ov.paid) return s; return s+(ov.amount!==undefined?ov.amount:r.amount); }
      if (r.catType === "manual_exp") return r.paid ? s : s + r.amount;
      return s;
    }, 0);
  }
  const totalExp = sumExpRows(filteredExpenseRows);
  const totalInc = filteredIncomeRows.reduce((s,r)=>s+(r.catType==="payouts"?r.payout||0:r.amount||0),0);

  // Previous month totals for trend
  const { exp: prevExp, inc: prevInc } = getMonthTotals(prevMonthKey);

  function trendBadge(cur, prev, inverse=false) {
    if (!prev) return null;
    const pct = Math.round(((cur-prev)/prev)*100);
    const up = pct > 0;
    const good = inverse ? !up : up;
    const color = pct===0 ? T.textMuted : good ? T.green : T.red;
    const tip = `${up?"Up":"Down"} ${Math.abs(pct)}% compared to previous month (${fmtFull(prev)} → ${fmtFull(cur)})`;
    return <Tooltip content={tip}><span style={{ fontSize:12,fontWeight:700,color,cursor:"help",display:"inline-flex",alignItems:"center",gap:3 }}>
      <span style={{ fontSize:14 }}>{good?"📈":"📉"}</span> {up?"▲":"▼"} {Math.abs(pct)}%
    </span></Tooltip>;
  }


  function handleDelay(delayAction) {
    if (!delayTarget) return;
    const { id, catType, item } = delayTarget;
    const originalDate = item?.date || null;
    const newDate = computeDelayedDate(originalDate, delayAction);
    if (!newDate) { setDelayTarget(null); return; }

    if (catType === "delivery") {
      // Store delayed date — computeDailyData reads delayedDate instead of original
      monthOps.updateDeliveryOverride(selectedMonth, id, { delayedDate: newDate, originalDate: originalDate });
    } else if (catType === "orders") {
      // Store delayed info in month data AND update via setMonthsDirect
      monthOps.setMonthsDirect?.({
        ...months,
        [selectedMonth]: {
          ...months[selectedMonth],
          orderExpDelayed: {
            ...(months[selectedMonth]?.orderExpDelayed || {}),
            [id]: { newDate, originalDate: originalDate },
          },
        },
      });
    } else if (catType === "recurring_exp" || catType === "recurring_inc") {
      // Store delayed date — computeDailyData reads delayedDate instead of original
      monthOps.updateRecurringOverride(selectedMonth, id, { delayedDate: newDate, originalDate: originalDate });
    } else if (catType === "manual_exp") {
      // Update the expense date directly
      const exps = (months[selectedMonth]?.expenses || []).map(ex =>
        ex.id === id ? { ...ex, date: newDate, originalDate: originalDate || ex.date } : ex
      );
      monthOps.setMonthsDirect?.({
        ...months,
        [selectedMonth]: { ...months[selectedMonth], expenses: exps },
      });
    }
    setDelayTarget(null);
  }

  // ── Export helpers ──
  function getExportTitle() {
    if (tfMode==="year") return `Year ${selectedYear}`;
    if (tfMode==="custom" && customFrom && customTo) return `${customFrom} to ${customTo}`;
    if (selectedMonths.length===1) return MONTH_NAMES[mo-1]+" "+yr+(selectedWeek?" Week "+selectedWeek:"");
    return `${selectedMonths.length} months`;
  }

  // Export uses filteredExpenseRows/filteredIncomeRows so it matches exactly what is on screen
  function buildExportRows(what) {
    const rows = [];
    if (what !== "income") {
      for (const row of filteredExpenseRows) {
        const key = row._month || selectedMonth;
        const [ky,km] = key.split("-").map(Number);
        const label = `${MONTH_NAMES[km-1]} ${ky}`;
        const kData = months[key]||{};
        let desc = row.what || row.name || row.description || "—";
        let amount = row.amount || 0;
        let status = "Pending";
        let category = "Other";
        if (row.catType==="delivery") { category="Fulfilment"; const ov=(kData.deliveryOverrides||{})[row.id]||{}; amount=ov.partialAmount!==undefined?ov.partialAmount:amount; status=ov.paid?"Paid":"Pending"; }
        if (row.catType==="orders") { category="Order"; status=(kData.orderExpPaid||{})[row.id]?"Paid":"Pending"; }
        if (row.catType==="recurring_exp") { category="Recurring"; const ov=(kData.recurringOverrides||{})[row.id||row.recurringId]||{}; amount=ov.amount!==undefined?ov.amount:amount; status=ov.paid?"Paid":"Pending"; }
        if (row.catType==="manual_exp") { category="Manual"; status=row.paid?"Paid":"Pending"; }
        rows.push([label,"Expense",category,desc,amount.toFixed(2),status]);
      }
    }
    if (what !== "expenses") {
      for (const row of filteredIncomeRows) {
        const key = row._month || selectedMonth;
        const [ky,km] = key.split("-").map(Number);
        const label = `${MONTH_NAMES[km-1]} ${ky}`;
        let desc = row.mktName || row.what || row.name || "—";
        let amount = row.payout || row.amount || 0;
        let category = "Other";
        let status = "Projected";
        if (row.catType==="payouts") category="Payout";
        if (row.catType==="recurring_inc") category="Recurring";
        if (row.catType==="manual_inc") { category="Manual"; status="—"; }
        rows.push([label,"Income",category,desc,amount.toFixed(2),status]);
      }
    }
    return rows;
  }

  function handleExportCSV(what) {
    const rows = [["Month","Type","Category","Description",`Amount (${currSym()})`, "Status"], ...buildExportRows(what)];
    const csv = rows.map(r=>r.map(c=>{ const s=String(c==null?"":c); return s.includes(",")||s.includes('"')?'"'+s.split('"').join('""')+'"':s; }).join(",")).join("\n");
    const blob = new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=`cashhorizon-${getExportTitle().split("").filter(c=>c>="a"&&c<="z"||c>="0"&&c<="9").join("").toLowerCase()}.csv`; a.click();
  }

  function handleExportPDF(what) {
    const rows = buildExportRows(what);
    const title = getExportTitle();
    const rowsHTML = rows.map(r=>{
      const isIncome = r[1]==="Income";
      const amtColor = isIncome?"#34c759":"#ff3b30";
      return `<tr>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:12px">${escHtml(r[0])}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:12px;color:${amtColor};font-weight:600">${escHtml(r[1])}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:12px;color:#666">${escHtml(r[2])}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:12px">${escHtml(r[3])}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:12px;text-align:right;font-weight:700;color:${amtColor}">${isIncome?"+":"-"}${escHtml(r[4])}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;color:${r[5]==="Paid"?"#34c759":r[5]==="Pending"?"#ff9500":"#86868b"}">${escHtml(r[5])}</td>
      </tr>`;
    }).join("");
    const totalExp = rows.filter(r=>r[1]==="Expense").reduce((s,r)=>s+parseFloat(r[4]||0),0);
    const totalInc = rows.filter(r=>r[1]==="Income").reduce((s,r)=>s+parseFloat(r[4]||0),0);
    const w = window.open("","_blank"); if(!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>CashHorizon — ${title}</title>
    <style>
      body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;padding:28px 32px;color:#111;max-width:900px;margin:0 auto}
      h2{font-size:24px;font-weight:800;margin:0 0 4px;letter-spacing:-0.5px}
      .meta{font-size:13px;color:#666;margin:0 0 24px}
      .summary{display:flex;gap:24px;margin-bottom:24px;padding:14px 16px;background:#f8f8f8;border-radius:8px}
      .sum-item{font-size:13px;color:#666}.sum-item strong{font-size:16px;display:block;margin-top:2px}
      table{width:100%;border-collapse:collapse}
      th{padding:8px 10px;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#888;border-bottom:2px solid #e5e5e5;font-weight:700;text-align:left}
      th:nth-child(5),th:nth-child(6){text-align:right}
      tr:hover td{background:#fafafa}
      @media print{@page{margin:15mm;size:A4}body{padding:0}.no-print{display:none}}
    </style></head><body>
    <h2>📊 ${title}</h2>
    <div class="meta">CashHorizon Export · Generated ${new Date().toLocaleDateString()} · ${what==="both"?"Expenses & Income":what==="expenses"?"Expenses only":"Income only"}</div>
    <div class="summary">
      ${what!=="income"?`<div class="sum-item">Total Expenses<strong style="color:#ff3b30">-${totalExp.toFixed(2)}</strong></div>`:""}
      ${what!=="expenses"?`<div class="sum-item">Total Income<strong style="color:#34c759">+${totalInc.toFixed(2)}</strong></div>`:""}
      ${what==="both"?`<div class="sum-item">Net<strong style="color:${totalInc-totalExp>=0?"#0071e3":"#ff3b30"}">${totalInc-totalExp>=0?"+":""}${(totalInc-totalExp).toFixed(2)}</strong></div>`:""}
    </div>
    <table>
      <thead><tr><th>Month</th><th>Type</th><th>Category</th><th>Description</th><th style="text-align:right">Amount</th><th style="text-align:right">Status</th></tr></thead>
      <tbody>${rowsHTML}</tbody>
    </table>
    <script>window.onload=()=>window.print();</script>
    </body></html>`);
    w.document.close();
  }

  const COL = isMobile ? "1fr 90px 100px" : "1fr 110px 210px";

  return (
    <div>
      <style>{`
        input:focus { box-shadow: 0 0 0 2px var(--focus-color, rgba(99,102,241,0.4)) !important; }
        .focus-expense input:focus { --focus-color: rgba(232,48,78,0.45); }
        .focus-income input:focus { --focus-color: rgba(0,168,107,0.45); }
        @keyframes chHighlightPulse {
          0%,100% { box-shadow: inset 0 0 0 2px #ef4444, 0 0 10px #ef444466; background: #ef444418; border-radius: 8px; }
          50%      { box-shadow: inset 0 0 0 3px #ef4444, 0 0 28px #ef444499; background: #ef444422; border-radius: 8px; }
        }
        @keyframes chMonthGlow {
          0%,100% { box-shadow: 0 0 0 3px var(--glow-a, rgba(0,122,255,0.2)), 0 0 18px var(--glow-b, rgba(0,122,255,0.35)), 0 0 36px var(--glow-c, rgba(0,122,255,0.14)); }
          50%      { box-shadow: 0 0 0 4px var(--glow-a, rgba(0,122,255,0.3)), 0 0 28px var(--glow-b, rgba(0,122,255,0.55)), 0 0 52px var(--glow-c, rgba(0,122,255,0.22)); }
        }
        @keyframes chBarGrow {
          0%   { transform:scaleY(0); }
          70%  { transform:scaleY(1.03); }
          100% { transform:scaleY(1); }
        }
        /* Month-icon drop — each circle falls from above and settles
           into the card. Combined with a per-card animation-delay this
           gives a fast left-to-right cascade (≈ 0.6 s end-to-end for
           12 cards). The final opacity is driven by the CSS variable
           --mi-final-opacity, set inline per icon so selected / current
           months can keep their slight saturation difference. */
        @keyframes monthIconDrop {
          from {
            opacity: 0;
            transform: translateY(-72px) scale(0.85);
          }
          to {
            opacity: var(--mi-final-opacity, 1);
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
      {/* Universal Add Transaction Modal */}
      <AddTransactionModal
        open={showNewAddModal}
        onClose={()=>setShowNewAddModal(false)}
        defaultType={addModalDefaults.type || "expense"}
        defaultCategory={addModalDefaults.category || "general"}
        selectedMonth={selectedMonth}
        visibleYears={visibleYears}
        recurringIncomeOps={recurringIncomeOps}
        recurringExpenseOps={recurringExpenseOps}
        setPartners={setPartners}
        partners={partners}
        monthOps={monthOps}
        onAddDecision={onAddDecision}
        onCreated={(id) => {
          if (!id) return;
          setHighlightId(id);
          setTimeout(() => setHighlightId(null), 4000);
        }}
      />

      {/* Add Transaction launcher moved inline with the search bar row
          (see further down). The standalone centered hero button was
          removed per user request — keeps the chrome tighter. */}

      {/* Delay Modal */}
      <DelayModal
        open={!!delayTarget}
        itemLabel={delayTarget?.item?.what || delayTarget?.item?.name || "Payment"}
        onClose={() => setDelayTarget(null)}
        onDelay={handleDelay}
      />


      {/* ── Timeframe Selector ── */}
      <Card style={{ marginBottom:12, padding:"10px 14px", background:"transparent", border:"none", boxShadow:"none", display:"flex", flexDirection:"column" }}>

        {/* Row 2: mode + year + week + exports (below months) */}
        <div className={isMobile?"sf-hscroll":""} style={{ order:2, marginTop:8, display:"flex", alignItems:"center", gap:6, flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : "visible" }}>
          {/* Mode pills */}
          {[["month","Month"],["multi","Multiple"],["year","Year"],["custom","Custom"]].map(([v,l])=>(
            <button key={v} onClick={()=>{
              setTfMode(v); setSelectedWeek(null);
              if (v==="year") setSelectedMonths(Array.from({length:12},(_,i)=>mkKey(selectedYear,i+1)));
              else if (v==="month") setSelectedMonths([currentKey]);
              else if (v==="custom") setSelectedMonths([]);
            }} style={{ padding:"7px 12px",borderRadius:RADIUS.pill,background:tfMode===v?T.blue+"22":"transparent",border:`1px solid ${tfMode===v?T.blue:T.border}`,color:tfMode===v?T.blue:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer",fontWeight:tfMode===v?700:500 }}>{l}</button>
          ))}

          {/* Divider before marketplace filter */}
          {marketplaces.length > 0 && <div style={{ width:1,height:16,background:T.border,flexShrink:0 }}/>}
          {/* Single-button marketplace dropdown with checkboxes for All /
              shared pools / individual marketplaces. Replaces the inline pill
              row to keep the toolbar tidy when there are many marketplaces. */}
          {marketplaces.length > 0 && (
            <MktSelector
              marketplaces={marketplaces}
              inventoryPools={inventoryPools}
              selMkts={selMkts}
              setSelMkts={setSelMkts}
              compact
            />
          )}

          {/* Divider */}
          {tfMode !== "custom" && <div style={{ width:1,height:16,background:T.border,flexShrink:0 }}/>}

          {/* Year pills */}
          {tfMode !== "custom" && visibleYears.slice().sort().map(y=>(
            <button key={y} onClick={()=>{
              setSelectedYear(y); setSelectedWeek(null);
              if (tfMode==="year") setSelectedMonths(Array.from({length:12},(_,i)=>mkKey(y,i+1)));
            }} style={{ padding:"7px 12px",borderRadius:RADIUS.pill,background:selectedYear===y?T.orange+"22":"transparent",border:`1px solid ${selectedYear===y?T.orange:T.border}`,color:selectedYear===y?T.orange:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer",fontWeight:selectedYear===y?700:500 }}>{y}</button>
          ))}

          {/* Week pills — inline */}
          {selectedMonths.length===1 && tfMode!=="custom" && (<>
            <div style={{ width:1,height:16,background:T.border,flexShrink:0 }}/>
            <button onClick={()=>setSelectedWeek(null)} style={{ padding:"5px 10px",borderRadius:RADIUS.pill,background:!selectedWeek?T.blue+"18":"transparent",border:`1px solid ${!selectedWeek?T.blue:T.border}`,color:!selectedWeek?T.blue:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer",fontWeight:!selectedWeek?700:400,flexShrink:0 }}>Full</button>
            {[1,2,3,4].map(w=>{
              const isSW=selectedWeek===w;
              const [yw,mw]=selectedMonth.split("-").map(Number);
              const ws=(w-1)*7+1, we=w===4?getDaysInMonth(yw,mw):w*7;
              return <button key={w} onClick={()=>setSelectedWeek(isSW?null:w)} style={{ padding:"5px 10px",borderRadius:RADIUS.pill,background:isSW?T.blue+"18":"transparent",border:`1px solid ${isSW?T.blue:T.border}`,color:isSW?T.blue:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer",fontWeight:isSW?700:400,flexShrink:0 }}>W{w}</button>;
            })}
          </>)}

          {/* Custom date inputs inline */}
          {tfMode==="custom" && (
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <DateInp value={customFrom} onChange={v=>setCustomFrom(v)} placeholder="From"/>
              <span style={{ color:T.textSub,fontSize:12 }}>→</span>
              <DateInp value={customTo} onChange={v=>setCustomTo(v)} placeholder="To"/>
            </div>
          )}

          {/* Spacer */}
          <div style={{ flex:1 }}/>

          {/* Summary + clear */}
          {selectedMonths.length>0 && (
            <span style={{ fontSize:12, color:T.textSub, flexShrink:0 }}>
              {tfMode==="year" ? "All of "+selectedYear :
               tfMode==="custom" ? (customFrom&&customTo?customFrom+" → "+customTo:"Custom range") :
               selectedMonths.length===1 ? MONTH_NAMES[mo-1]+" "+yr+(selectedWeek?", W"+selectedWeek:"") :
               selectedMonths.length+" mo."}
            </span>
          )}
          {selectedMonths.length>1 && tfMode!=="year" && (
            <button onClick={()=>{ setSelectedMonths([currentKey]); setTfMode("month"); }} style={{ background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:12,fontFamily:FONT,flexShrink:0 }}>Clear</button>
          )}

          {/* Add Transaction button moved to the big centered launcher
              above the timeframe card — see the popover near the top of
              this page. The toolbar now only holds filters + exports. */}

          {/* CSV export */}
          <div style={{ position:"relative", flexShrink:0 }}>
            <button onClick={()=>setShowExportMenu(v=>v==="csv"?null:"csv")} style={{ display:"flex",alignItems:"center",gap:4,padding:"7px 9px",borderRadius:RADIUS.md,background:"transparent",border:`1px solid ${T.border}`,color:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer" }}>CSV <span style={{ fontSize:8,opacity:0.6 }}>▼</span></button>
            {showExportMenu==="csv" && (
              <><div style={{ position:"fixed",inset:0,zIndex:499 }} onClick={()=>setShowExportMenu(null)}/>
              <div style={{ position:"absolute",top:"calc(100% + 4px)",right:0,zIndex:500,background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.md,boxShadow:"0 12px 36px rgba(0,0,0,0.3)",minWidth:150,overflow:"hidden" }}>
                {[["both","📋 Both"],["expenses","💸 Expenses"],["income","💰 Income"]].map(([v,l])=>(
                  <button key={v} onClick={()=>{ handleExportCSV(v); setShowExportMenu(null); }} style={{ display:"block",width:"100%",padding:"9px 14px",background:"transparent",border:"none",cursor:"pointer",fontFamily:FONT,fontSize:12,color:T.text,textAlign:"left" }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{l}</button>
                ))}
              </div></>
            )}
          </div>

          {/* PDF export */}
          <div style={{ position:"relative", flexShrink:0 }}>
            <button onClick={()=>setShowExportMenu(v=>v==="pdf"?null:"pdf")} style={{ display:"flex",alignItems:"center",gap:4,padding:"7px 9px",borderRadius:RADIUS.md,background:"transparent",border:`1px solid ${T.border}`,color:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer" }}>PDF <span style={{ fontSize:8,opacity:0.6 }}>▼</span></button>
            {showExportMenu==="pdf" && (
              <><div style={{ position:"fixed",inset:0,zIndex:499 }} onClick={()=>setShowExportMenu(null)}/>
              <div style={{ position:"absolute",top:"calc(100% + 4px)",right:0,zIndex:500,background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.md,boxShadow:"0 12px 36px rgba(0,0,0,0.3)",minWidth:150,overflow:"hidden" }}>
                {[["both","📋 Both"],["expenses","💸 Expenses"],["income","💰 Income"]].map(([v,l])=>(
                  <button key={v} onClick={()=>{ handleExportPDF(v); setShowExportMenu(null); }} style={{ display:"block",width:"100%",padding:"9px 14px",background:"transparent",border:"none",cursor:"pointer",fontFamily:FONT,fontSize:12,color:T.text,textAlign:"left" }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{l}</button>
                ))}
              </div></>
            )}
          </div>

          {/* Full data export */}
          <button onClick={() => setExportOpen(true)} style={{ display:"flex",alignItems:"center",gap:4,padding:"4px 9px",borderRadius:RADIUS.md,background:"transparent",border:`1px solid ${T.border}`,color:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap" }}>📤 Export</button>
          <button onClick={()=>setComparisonView(v=>!v)} style={{ display:"flex",alignItems:"center",gap:4,padding:"4px 9px",borderRadius:RADIUS.md,background:comparisonView?T.accent+"18":"transparent",border:`1px solid ${comparisonView?T.accent:T.border}`,color:comparisonView?T.accent:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer",flexShrink:0,fontWeight:comparisonView?700:400,whiteSpace:"nowrap" }}>📊 Comparison</button>
        </div>

        {/* Row 1: Month cards (above options) — paddingTop reserves room
            for the month-icon circle that protrudes above each card. The
            circle is full card-width and its top half sticks out, so the
            row needs roughly half a card-width of space above. */}
        {tfMode !== "custom" && (
          <div data-tour="calendar-months" className={isMobile?"sf-hscroll":""} style={{ order:1, display:"flex", gap:6, overflowX: isMobile ? "auto" : "visible", overflowY: "visible", paddingTop: 56, paddingBottom: isMobile ? 4 : 0 }}>
            {(()=>{
              const allTotals = Array.from({length:12},(_,i)=>getMonthTotals(mkKey(selectedYear, i+1)));
              const maxAmt = Math.max(1,...allTotals.map(t=>Math.max(t.inc,t.exp)));
              return Array.from({length:12},(_,i)=>{
              const key = mkKey(selectedYear, i+1);
              const {exp:e, inc:inc_} = allTotals[i];
              const isSel = selectedMonths.includes(key);
              const isCur = key === currentKey;
              const incPct = Math.round(inc_/maxAmt*100);
              const expPct = Math.round(e/maxAmt*100);
              return (
                <button
                  key={key}
                  onClick={()=>{
                    setSelectedWeek(null);
                    if (tfMode==="month") setSelectedMonths([key]);
                    else if (tfMode==="multi") setSelectedMonths(prev=>prev.includes(key)?prev.filter(k=>k!==key):[...prev,key].sort());
                  }}
                  style={{
                    flex: isMobile ? "0 0 auto" : 1, minWidth: isMobile ? 84 : 0, height:129,
                    borderRadius:RADIUS.lg,
                    background: isSel ? T.blue+"1a"
                      : isCur ? T.teal+"14"
                      : T.card,
                    border:`2px solid ${isSel ? T.blue : isCur ? T.teal+"88" : T.border}`,
                    boxShadow: isSel
                      ? `0 0 0 3px ${T.blue}33, 0 0 18px ${T.blue}55, 0 0 36px ${T.blue}22`
                      : isCur ? `0 0 8px ${T.teal}33` : "none",
                    cursor:"pointer", fontFamily:FONT,
                    display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-start",
                    gap:2,
                    // No left/right padding — the icon spans the full card
                    // width so its left/right edges touch the card edges,
                    // like the user-supplied Month View.png.
                    padding:"0 0 6px",
                    transition:`all 0.3s ${SPRING}`,
                    position:"relative", overflow:"visible",
                    animation: isSel ? "chMonthGlow 2s ease-in-out infinite" : "none",
                  }}
                >
                  {isCur && <div style={{ position:"absolute",bottom:5,right:5,width:5,height:5,borderRadius:"50%",background:T.teal,boxShadow:`0 0 5px ${T.teal}`,zIndex:2 }}/>}
                  {/* Circular month illustration — full card width, half
                      above the card and half inside. MonthIcon handles
                      the fade-in so the icon doesn't pop in after a
                      split-second blank moment on mount or tab switch. */}
                  <MonthIcon index={i} isSel={isSel} isCur={isCur}/>
                  <span style={{ fontSize:isSel?13:12, fontWeight:isSel?800:600, color:isSel?T.blue:isCur?T.teal:T.text, transition:"all 0.2s", letterSpacing:isSel?0.3:0 }}>{["January","February","March","April","May","June","July","August","September","October","November","December"][i]}</span>
                  {inc_>0 && <span style={{ fontSize:14, fontWeight:600, color:T.green }}>+{fmt(inc_)}</span>}
                  {e>0 && <span style={{ fontSize:14, fontWeight:600, color:T.red }}>-{fmt(e)}</span>}
                </button>
              );
            });})()}
          </div>
        )}

        {/* Week pills moved to control row above */}

      </Card>

      <style>{`@keyframes ccBarGrow{from{transform:scaleY(0)}to{transform:scaleY(1)}}`}</style>

      <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:12, flexWrap:"wrap" }}>
        {/* Filter — first (left) per user. Popover panel anchors to
            this position:relative wrapper via position:absolute. */}
        <div style={{ position:"relative", order: 1 }}>
          <button onClick={()=>setFilterDropOpen(o=>!o)} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 12px", borderRadius:RADIUS.md, background:T.surface, border:`1px solid ${!filterTypes.includes("all")?T.blue:T.border}`, color:!filterTypes.includes("all")?T.blue:T.text, fontSize:13, fontFamily:FONT, cursor:"pointer", fontWeight:!filterTypes.includes("all")?700:400 }}>
            {filterTypes.includes("all") ? "All transactions" : `${filterTypes.length} filter${filterTypes.length>1?"s":""}`}
            <span style={{ fontSize:12, opacity:0.6 }}>{filterDropOpen?"▲":"▼"}</span>
          </button>
          {filterDropOpen && (<>
            <div onClick={()=>setFilterDropOpen(false)} style={{ position:"fixed", inset:0, zIndex:500 }}/>
            <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:600, background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, boxShadow:"0 12px 40px rgba(0,0,0,0.35)", width:220, overflow:"hidden", animation:"dashMenuIn 0.2s cubic-bezier(0.34,1.4,0.64,1) both" }}>
              {[
                {v:"all",l:"All transactions"},
                {v:"unpaid",l:"Unpaid"},
                {v:"paid",l:"Paid"},
                {v:"manual",l:"Manual"},
                {v:"delivery",l:"Fulfilment"},
                {v:"orders",l:"Orders"},
                {v:"recurring",l:"Recurring"},
                {v:"payouts",l:"Payouts"},
                {v:"planner",l:"Planner"},
              ].map(({v,l})=>{
                const checked = v==="all" ? filterTypes.includes("all") : filterTypes.includes(v);
                return (
                  <button key={v} onClick={()=>toggleFilter(v)} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:checked?T.blue+"10":"transparent", border:"none", cursor:"pointer", fontFamily:FONT, fontSize:13, color:checked?T.blue:T.text, fontWeight:checked?700:400, textAlign:"left", borderBottom:`1px solid ${T.border}22` }}>
                    <div style={{ width:16, height:16, borderRadius:3, border:`1.5px solid ${checked?T.blue:T.border}`, background:checked?T.blue:"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:"#fff", flexShrink:0 }}>
                      {checked?"✓":""}
                    </div>
                    {l}
                  </button>
                );
              })}
            </div>
          </>)}
        </div>
        {/* Search — grows to fill the middle of the row. */}
        <div style={{ position:"relative", flex:"1 1 220px", minWidth:180, order: 2 }}>
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:14, color:T.textSub, pointerEvents:"none" }}>🔍</span>
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search transactions…"
            style={{ width:"100%", background:"transparent", border:`1.5px solid ${searchQuery?T.blue:T.border}`, borderRadius:RADIUS.md, color:T.text, padding:"8px 12px 8px 32px", fontSize:13, outline:"none", fontFamily:FONT, boxSizing:"border-box", transition:"border-color 0.15s" }}
          />
          {searchQuery && <button onClick={() => setSearchQuery("")} style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:T.textMuted, cursor:"pointer", fontSize:14, lineHeight:1 }}>✕</button>}
        </div>
        {/* Add Transaction launcher — pinned to the RIGHT end of the
            row. Approximately 2x the previous width per user: minWidth
            300 px (or 100% on narrow mobile flex-wrap rows). */}
        {(!can || can("calendar","edit")) && (
          <div data-tour="add-transaction" style={{ position:"relative", order: 3 }}>
            <button
              onClick={() => setAddPopoverOpen(o => !o)}
              style={{
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                padding:"8px 24px", borderRadius: RADIUS.md,
                minWidth: 300,
                background: T.accent, color:"#fff", border:"none",
                fontSize: 13, fontFamily: FONT, fontWeight: 700,
                cursor:"pointer", whiteSpace:"nowrap",
                boxShadow:`0 4px 14px ${T.accent}44`,
                transition:"transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow=`0 6px 18px ${T.accent}55`; }}
              onMouseLeave={e=>{ e.currentTarget.style.transform="translateY(0)";  e.currentTarget.style.boxShadow=`0 4px 14px ${T.accent}44`; }}
            >
              <span style={{ fontSize: 14, lineHeight: 1 }}>＋</span>
              Add Transaction
              <span style={{ fontSize: 10, opacity: 0.75, marginLeft: 2 }}>▾</span>
            </button>
            {addPopoverOpen && (
              <>
                <div onClick={() => setAddPopoverOpen(false)} style={{
                  position:"fixed", inset:0, zIndex:499, background:"transparent",
                }}/>
                <div role="menu" style={{
                  position:"absolute", top:"calc(100% + 8px)",
                  left:"50%", transform:"translateX(-50%)",
                  zIndex:500, minWidth: 240,
                  background: T.card, border:`1px solid ${T.border}`,
                  borderRadius: RADIUS.lg, boxShadow:"0 16px 40px rgba(0,0,0,0.32)",
                  padding:6, animation:"sfFadeIn 0.15s ease both",
                }}>
                  {[
                    { key:"add",     emoji:"＋", label:"Add Transaction", sub:"Open the universal form",      color: T.accent, onPick: () => pickAddOption() },
                    { key:"income",  emoji:"💰", label:"Income",          sub:"One-time or recurring income", color: T.green,  onPick: () => pickAddOption({ type:"income",  category:"general" }) },
                    { key:"expense", emoji:"💸", label:"Expense",         sub:"One-time or recurring expense", color: T.red,    onPick: () => pickAddOption({ type:"expense", category:"general" }) },
                    { key:"partner", emoji:"🤝", label:"Partner",         sub:"VA, agency, contractor",       color: T.purple, onPick: () => pickAddOption({ type:"expense", category:"partner" }) },
                    { key:"credit",  emoji:"🏦", label:"Credit Line",     sub:"Loan or revolving credit",     color: T.orange, onPick: () => pickAddOption({ type:"expense", category:"credit"  }) },
                    { key:"vat",     emoji:"🇪🇺", label:"VAT Payment",     sub:"Quarterly EU template (Jan/Apr/Jul/Oct)", color: T.teal,   onPick: addVatPreset },
                  ].map(opt => (
                    <button key={opt.key} onClick={opt.onPick}
                      style={{
                        display:"flex", alignItems:"center", gap:10,
                        width:"100%", textAlign:"left",
                        padding:"10px 12px", borderRadius: RADIUS.md,
                        background:"transparent", border:"none", cursor:"pointer",
                        fontFamily: FONT,
                      }}
                      onMouseEnter={e=>e.currentTarget.style.background = T.surfaceHi}
                      onMouseLeave={e=>e.currentTarget.style.background = "transparent"}
                    >
                      <span style={{
                        width:30, height:30, borderRadius:"50%",
                        display:"inline-flex", alignItems:"center", justifyContent:"center",
                        background: opt.color+"18", color: opt.color, fontSize:16, flexShrink:0,
                      }}>{opt.emoji}</span>
                      <span style={{ flex:1, minWidth:0 }}>
                        <span style={{ display:"block", fontSize:13, fontWeight:700, color:T.text }}>{opt.label}</span>
                        <span style={{ display:"block", fontSize:11, color:T.textSub, marginTop:1 }}>{opt.sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Bar charts rendered inside grid center column below */}

      {/* ── Year Comparison View ── */}
      {comparisonView && (()=>{
        const yearA = selectedYear - 1, yearB = selectedYear;
        const FULL_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const data = Array.from({length:12},(_,i)=>{
          const kA=mkKey(yearA,i+1), kB=mkKey(yearB,i+1);
          const tA=getMonthTotals(kA), tB=getMonthTotals(kB);
          return { m:i, kA, kB, incA:tA.inc, expA:tA.exp, netA:tA.inc-tA.exp, incB:tB.inc, expB:tB.exp, netB:tB.inc-tB.exp };
        });
        const maxVal = Math.max(1,...data.flatMap(d=>[d.incA,d.incB,d.expA,d.expB]));
        const pct = (a,b) => a>0?Math.round(((b-a)/a)*100):b>0?100:0;
        const arrow = (p) => Math.abs(p)<5 ? {icon:"━",color:T.textMuted} : p>0 ? {icon:"▲",color:T.green} : {icon:"▼",color:T.red};
        const tip = (label,mName,vA,vB,yA,yB,p) => `${label} in ${mName} ${yB} was ${fmtFull(vB)} — ${Math.abs(p)}% ${p>=0?"more":"less"} than ${mName} ${yA} (${fmtFull(vA)})`;
        // Sparkline
        const netAll = data.flatMap(d=>[d.netA,d.netB]);
        const spMin=Math.min(...netAll), spMax=Math.max(...netAll);
        const spRange=Math.max(1,spMax-spMin);
        const spW=800, spH=60;
        const spY=(v)=>spH-8-((v-spMin)/spRange)*(spH-16);
        const lineA=data.map((d,i)=>`${i*(spW/11)},${spY(d.netA)}`).join(" ");
        const lineB=data.map((d,i)=>`${i*(spW/11)},${spY(d.netB)}`).join(" ");

        return (
          <div style={{ marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{yearA} vs {yearB} — Year Comparison</span>
            </div>
            <div className={isMobile?"sf-hscroll":""} style={{ overflowX:isMobile?"auto":"visible" }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(12,1fr)", gap:4, minWidth:isMobile?960:0 }}>
              {data.map((d,i)=>{
                const netPct=pct(Math.abs(d.netA)||1,Math.abs(d.netB)||1);
                const netImproved=d.netB>d.netA;
                const bgTint=d.netA===0&&d.netB===0?"transparent":netImproved?T.green:T.red;
                const bgOpacity=Math.min(0.15,Math.abs(netPct)/500);
                return (
                  <div key={i} style={{ background:`${bgTint}${Math.round(bgOpacity*255).toString(16).padStart(2,"0")}`, borderRadius:RADIUS.md, padding:"8px 4px", textAlign:"center", border:`1px solid ${T.border}33` }}>
                    <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:6 }}>{FULL_MONTHS[i]}</div>
                    <div style={{ fontSize:12, color:T.textMuted, marginBottom:6 }}>{yearA} | {yearB}</div>
                    {/* Income */}
                    <div style={{ marginBottom:6 }}>
                      <div style={{ fontSize:12, color:T.textSub, fontWeight:600, marginBottom:3 }}>Income</div>
                      <div style={{ display:"flex", justifyContent:"center", alignItems:"flex-end", gap:3, height:24 }}>
                        <div style={{ width:12, height:`${Math.max(3,d.incA/maxVal*24)}px`, background:T.green, opacity:0.3, borderRadius:2 }}/>
                        <div style={{ width:12, height:`${Math.max(3,d.incB/maxVal*24)}px`, background:T.green, borderRadius:2 }}/>
                      </div>
                      <div style={{ fontSize:12, color:T.textSub }}>{fmt(d.incA)} | <strong style={{color:T.green}}>{fmt(d.incB)}</strong></div>
                      {(()=>{ const p=pct(d.incA,d.incB); const a=arrow(p); return <Tooltip content={tip("Income",FULL_MONTHS[i],d.incA,d.incB,yearA,yearB,p)}><span style={{ fontSize:12, fontWeight:700, color:a.color, cursor:"help" }}>{a.icon} {Math.abs(p)}%</span></Tooltip>; })()}
                    </div>
                    {/* Expenses */}
                    <div style={{ marginBottom:6 }}>
                      <div style={{ fontSize:12, color:T.textSub, fontWeight:600, marginBottom:3 }}>Expenses</div>
                      <div style={{ display:"flex", justifyContent:"center", alignItems:"flex-end", gap:3, height:24 }}>
                        <div style={{ width:12, height:`${Math.max(3,d.expA/maxVal*24)}px`, background:T.red, opacity:0.3, borderRadius:2 }}/>
                        <div style={{ width:12, height:`${Math.max(3,d.expB/maxVal*24)}px`, background:T.red, borderRadius:2 }}/>
                      </div>
                      <div style={{ fontSize:12, color:T.textSub }}>{fmt(d.expA)} | <strong style={{color:T.red}}>{fmt(d.expB)}</strong></div>
                      {(()=>{ const p=pct(d.expA,d.expB); const a=arrow(p); return <Tooltip content={tip("Expenses",FULL_MONTHS[i],d.expA,d.expB,yearA,yearB,p)}><span style={{ fontSize:12, fontWeight:700, color:a.color, cursor:"help" }}>{a.icon} {Math.abs(p)}%</span></Tooltip>; })()}
                    </div>
                    {/* Net */}
                    <div style={{ borderTop:`1px solid ${T.border}33`, paddingTop:4 }}>
                      <div style={{ fontSize:12, color:T.textSub, fontWeight:600, marginBottom:2 }}>Net</div>
                      <div style={{ fontSize:13, fontWeight:700, color:d.netB>=0?T.green:T.red }}>{d.netB>=0?"+":""}{fmt(d.netB)}</div>
                      {(()=>{ const p=pct(Math.abs(d.netA)||1,Math.abs(d.netB)||1); const a=arrow(d.netB>d.netA?1:-1); return d.netA!==0?<Tooltip content={tip("Net cashflow",FULL_MONTHS[i],d.netA,d.netB,yearA,yearB,p)}><span style={{ fontSize:12, fontWeight:700, color:a.color, cursor:"help" }}>{a.icon} {Math.abs(p)}%</span></Tooltip>:null; })()}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
            {/* Sparkline */}
            <div style={{ marginTop:12, background:T.card, borderRadius:RADIUS.md, padding:"8px 12px", border:`1px solid ${T.border}33` }}>
              <div style={{ fontSize:12, color:T.textSub, fontWeight:600, marginBottom:4 }}>Net Cashflow Trajectory</div>
              <svg viewBox={`0 0 ${spW} ${spH}`} style={{ width:"100%", height:60 }}>
                <polyline points={lineA} fill="none" stroke={T.textMuted} strokeWidth={1.5} strokeDasharray="4 3" opacity={0.5}/>
                <polyline points={lineB} fill="none" stroke={T.accent} strokeWidth={2}/>
                {data.map((d,i)=>(<g key={i}>
                  <circle cx={i*(spW/11)} cy={spY(d.netA)} r={2.5} fill={T.textMuted} opacity={0.5}/>
                  <circle cx={i*(spW/11)} cy={spY(d.netB)} r={3} fill={T.accent}/>
                </g>))}
              </svg>
              <div style={{ display:"flex", justifyContent:"center", gap:16, marginTop:4 }}>
                <span style={{ fontSize:12, color:T.textMuted }}>--- {yearA}</span>
                <span style={{ fontSize:12, color:T.accent, fontWeight:600 }}>── {yearB}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Layout: Income (left) | Expenses (right) — hidden in comparison view ── */}
      {!comparisonView && (
      <div style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 200px 1fr", gap: 12, alignItems:"start" }}>

      {/* ── EXPENSES TABLE ── */}
      <div data-tour="calendar-expenses" style={{ borderRadius:RADIUS.lg, overflow:"visible", border:"1px solid "+T.red+"44", marginBottom: isMobile ? 20 : 0, background:T.card, order: isMobile ? 1 : 3 }}>
        {/* Header */}
        <div style={{ display:"grid", gridTemplateColumns:COL, padding:"10px 16px", background:"transparent", borderBottom:`1px solid ${T.red}33` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:13, fontWeight:800, color:T.red, letterSpacing:1, textTransform:"uppercase" }}>💸 Expenses ({filteredExpenseRows.length}{filteredExpenseRows.length !== expenseRows.length ? "/"+expenseRows.length : ""})</span>
          </div>
          <span style={{ fontSize:12, fontWeight:700, color:T.red, textAlign:"right", letterSpacing:0.5, textTransform:"uppercase" }}>Amount</span>
          <span style={{ fontSize:12, fontWeight:700, color:T.red, textAlign:"right", paddingRight:8, letterSpacing:0.5, textTransform:"uppercase" }}>Action</span>
        </div>
        {/* Paid vs Remaining + Bulk Mark Paid */}
        {totalExp > 0 && (
          <div style={{ padding:"8px 16px", borderBottom:`1px solid ${T.border}33`, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <div style={{ flex:1, minWidth:150 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                <span style={{ fontSize:12, color:T.green, fontWeight:700 }}>{fmtFull(paidT)} paid</span>
                <span style={{ fontSize:12, color:T.textMuted }}>of</span>
                <span style={{ fontSize:12, color:T.text, fontWeight:600 }}>{fmtFull(totalExp+paidT)}</span>
                <span style={{ fontSize:12, color:T.textMuted }}>—</span>
                <span style={{ fontSize:12, color:T.red, fontWeight:700 }}>{fmtFull(totalExp)} remaining</span>
              </div>
              <div style={{ height:4, borderRadius:2, background:T.surfaceHi, overflow:"hidden" }}>
                <div style={{ height:"100%", borderRadius:2, background:T.green, width:`${Math.min(100, Math.round(paidT/(totalExp+paidT)*100))}%`, transition:"width 0.3s ease" }}/>
              </div>
            </div>
            <ConfirmSheet open={confirmMarkAllPaid} icon="✓" title="Mark all as paid?" message={`Mark all ${filteredExpenseRows.filter(r=>r.catType!=="payouts").length} unpaid expenses as paid?`} confirmLabel="Yes, mark all paid" onConfirm={()=>{
              setConfirmMarkAllPaid(false);
              for (const row of filteredExpenseRows) {
                if (row.catType==="manual_exp" && !row.paid) monthOps.toggleExpensePaid(row._month||selectedMonth, row.id);
                else if (row.catType==="delivery") { const ov=(mData.deliveryOverrides||{})[row.id]||{}; if(!ov.paid) monthOps.updateDeliveryOverride(row._month||selectedMonth, row.id, {paid:true}); }
                else if (row.catType==="orders") { if(!(mData.orderExpPaid||{})[row.id]) monthOps.toggleOrderExpPaid(row._month||selectedMonth, row.id); }
                else if (row.catType==="recurring_exp") { const ov=(mData.recurringOverrides||{})[row.id||row.recurringId]||{}; if(!ov.paid) monthOps.updateRecurringOverride(row._month||selectedMonth, row.id||row.recurringId, {paid:true}); }
              }
            }} onCancel={()=>setConfirmMarkAllPaid(false)}/>
            {totalExp > 0 && filteredExpenseRows.some(r=>r.catType!=="payouts") && (!can || can("calendar","edit")) && (
              <button onClick={()=>setConfirmMarkAllPaid(true)}
                style={{ padding:"5px 12px", borderRadius:RADIUS.sm, fontSize:12, fontFamily:FONT, cursor:"pointer", background:T.green+"15", border:`1px solid ${T.green}30`, color:T.green, fontWeight:600, whiteSpace:"nowrap" }}>
                ✓ Mark all paid
              </button>
            )}
          </div>
        )}
        {/* Column labels */}
        <div style={{ display:"grid", gridTemplateColumns:COL, padding:"6px 16px", background:"transparent", borderBottom:`1px solid ${T.border}33` }}>
          <span style={{ fontSize:12, color:T.textSub, fontWeight:600 }}>Date · Description</span>
          <span style={{ fontSize:12, color:T.textSub, fontWeight:600, textAlign:"right" }}>This month</span>
          <span style={{ fontSize:12, color:T.textSub, fontWeight:600, textAlign:"right", paddingRight:8 }}></span>
        </div>
        {filteredExpenseRows.length === 0
          ? <div style={{ padding:"20px 16px", color:T.textSub, fontSize:14, textAlign:"center", background:"transparent" }}>{searchQuery || filterType!=="all" ? "No matching transactions" : "No expenses this month"}</div>
          : filteredExpenseRows.map((row, i) => (
            <TableRow can={can} key={`${row._month||selectedMonth}_${row.id||row.recurringId||i}`}
              item={row} catType={row.catType} isExpense={true}
              mData={months[row._month||selectedMonth]||{}}
              months={months} selectedMonth={row._month||selectedMonth}
              monthOps={monthOps} prevMData={prevMData} T={T}
              onDelayClick={(id, catType, item) => setDelayTarget({ id, catType, item })}
              orders={orders} onNavigate={onNavigate}
              recurringExpenseOps={recurringExpenseOps} recurringIncomeOps={recurringIncomeOps}
              highlightId={highlightId}
              setReminderTarget={setReminderTarget}
              onConfirmDelete={setCalConfirmDelete}
              onMarkPaid={markPaidWithPrompt}
            />
          ))
        }
        {/* Total row */}
        <div style={{ display:"grid", gridTemplateColumns:COL, padding:"12px 16px", background:"transparent", borderTop:`2px solid ${T.red}` }}>
          <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Total Expenses</span>
          <span style={{ fontSize:16, fontWeight:800, color:T.red, textAlign:"right", fontVariantNumeric:"tabular-nums" }}>-{fmtFull(totalExp)}</span>
          <div style={{ textAlign:"right", paddingRight:8 }}>{trendBadge(totalExp, prevExp, true)}</div>
        </div>
      </div>

      {/* ── INCOME TABLE ── */}
      <div data-tour="calendar-income" style={{ borderRadius:RADIUS.lg, overflow:"visible", border:"1px solid "+T.green+"44", marginBottom: isMobile ? 20 : 0, background:T.card, order: isMobile ? 2 : 1 }}>
        <div style={{ display:"grid", gridTemplateColumns:COL, padding:"10px 16px", background:"transparent", borderBottom:`1px solid ${T.green}33` }}>
          <span style={{ fontSize:13, fontWeight:800, color:T.green, letterSpacing:1, textTransform:"uppercase" }}>💰 Income ({filteredIncomeRows.length}{filteredIncomeRows.length !== incomeRows.length ? "/"+incomeRows.length : ""})</span>
          <span style={{ fontSize:12, fontWeight:700, color:T.green, textAlign:"right", letterSpacing:0.5, textTransform:"uppercase" }}>Amount</span>
          <span style={{ fontSize:12, fontWeight:700, color:T.green, textAlign:"right", paddingRight:8, letterSpacing:0.5, textTransform:"uppercase" }}>Status</span>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:COL, padding:"6px 16px", background:"transparent", borderBottom:`1px solid ${T.border}33` }}>
          <span style={{ fontSize:12, color:T.textSub, fontWeight:600 }}>Date · Description</span>
          <span style={{ fontSize:12, color:T.textSub, fontWeight:600, textAlign:"right" }}>This month</span>
          <span style={{ fontSize:12, color:T.textSub, fontWeight:600, textAlign:"right", paddingRight:8 }}></span>
        </div>
        {filteredIncomeRows.length === 0
          ? <div style={{ padding:"20px 16px", color:T.textSub, fontSize:14, textAlign:"center", background:"transparent" }}>No income this month</div>
          : filteredIncomeRows.map((row, i) => (
            <TableRow can={can} key={`${row._month||selectedMonth}_${row.id||row.recurringId||row.mktId||i}`}
              item={row} catType={row.catType} isExpense={false}
              mData={months[row._month||selectedMonth]||{}}
              months={months} selectedMonth={row._month||selectedMonth}
              monthOps={monthOps} prevMData={prevMData} T={T}
              onDelayClick={(id, catType, item) => setDelayTarget({ id, catType, item })}
              orders={orders} onNavigate={onNavigate}
              recurringExpenseOps={recurringExpenseOps} recurringIncomeOps={recurringIncomeOps}
              highlightId={highlightId}
              setReminderTarget={setReminderTarget}
              onConfirmDelete={setCalConfirmDelete}
              onMarkPaid={markPaidWithPrompt}
            />
          ))
        }
        <div style={{ display:"grid", gridTemplateColumns:COL, padding:"12px 16px", background:"transparent", borderTop:`2px solid ${T.green}` }}>
          <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Total Income</span>
          <span style={{ fontSize:16, fontWeight:800, color:T.green, textAlign:"right", fontVariantNumeric:"tabular-nums" }}>+{fmtFull(totalInc)}</span>
          <div style={{ textAlign:"right", paddingRight:8 }}>{trendBadge(totalInc, prevInc)}</div>
        </div>
      </div>

      {/* Center column: bars + Net Cash Flow — sticky to bottom */}
      {!isMobile && (
        <div style={{ order:2, position:"sticky", bottom:16, alignSelf:"end", display:"flex", flexDirection:"column", alignItems:"center", transformOrigin:"bottom", animation:"ccBarGrow 1s cubic-bezier(0.34,1.56,0.64,1) both", maxWidth:200, margin:"0 auto", overflow:"visible" }}>
          {/* Net Cash Flow */}
          <div style={{ textAlign:"center", marginBottom:32, whiteSpace:"nowrap" }}>
            <div style={{ fontSize:16, color:T.text, fontWeight:700, marginBottom:2 }}>Net Cash Flow</div>
            <div style={{ fontSize:12, color:T.textSub, fontWeight:500, marginBottom:4 }}>this period</div>
            <div style={{ fontSize:20, fontWeight:800, color:(totalInc-totalExp)>=0?T.green:T.red }}>
              {(totalInc-totalExp)>=0?"+":""}{fmtFull(totalInc-totalExp)}
            </div>
          </div>
          {/* Bars with ghost comparison */}
          <div style={{ display:"flex", alignItems:"flex-end", gap:10 }}>
            {[
              { val:totalInc, color:T.green, delay:0 },
              { val:totalExp, color:T.red, delay:0.2 },
            ].map(({val,color,delay},bi) => {
              const maxRef = Math.max(totalInc,totalExp,1);
              const maxH = Math.max(16, Math.min(Math.round(window.innerHeight * 0.35), val/maxRef * Math.round(window.innerHeight * 0.35)));
              const valStr = fmtFull(val);
              return (
                <div key={bi} style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                  <div style={{ marginBottom:6, display:"flex" }}>
                    {valStr.split("").map((ch,ci) => (
                      <span key={ci} style={{ display:"inline-block", fontSize:14, fontWeight:700, color }}>{ch}</span>
                    ))}
                  </div>
                  <div style={{ width:72, height:maxH, background:color, borderRadius:6, transition:"height 0.5s ease" }}/>
                </div>
              );
            })}
          </div>
        </div>
      )}

      </div>)}{/* end grid + comparison conditional */}



      {/* Reminder Modal */}
      <ReminderModal
        open={!!reminderTarget}
        onClose={()=>setReminderTarget(null)}
        attachedTo={reminderTarget ? { kind:String(reminderTarget.kind||""), id:String(reminderTarget.id||""), label:String(reminderTarget.label||"") } : null}
        initialTitle={String(reminderTarget?.label||"")}
      />

      {/* Delete confirmation */}
      <ConfirmSheet
        open={!!calConfirmDelete}
        icon="🗑"
        title={`Delete "${calConfirmDelete?.label || "item"}"?`}
        message="This action cannot be undone."
        confirmLabel="Yes, Delete"
        onConfirm={()=>{ calConfirmDelete?.action?.(); setCalConfirmDelete(null); }}
        onCancel={()=>setCalConfirmDelete(null)}
      />

      {/* Export modal */}
      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        months={months}
        orders={orders}
        marketplaces={marketplaces}
        visibleYears={visibleYears}
        recurringIncome={recurringIncome}
        recurringExpenses={recurringExpenses}
        partners={partners}
        deliveryCreditLimit={deliveryCreditLimit}
        fbmSettings={fbmSettings}
        account={account}
      />

      {/* Mobile FAB — opens the same popover as the desktop launcher so
          mobile users get the full menu (Income / Expense / Partner /
          Credit Line / VAT / generic Add) without needing to scroll to
          the top of the page. */}
      {isMobile && (!can || can("calendar","edit")) && (
        <button onClick={() => {
          // The popover is anchored to the big button at the top of the
          // page, so scroll there before opening or the menu would appear
          // off-screen.
          window.scrollTo({ top: 0, behavior: "smooth" });
          setAddPopoverOpen(o => !o);
        }}
          style={{
            position:"fixed", bottom:80, right:18, zIndex:200,
            width:52, height:52, borderRadius:"50%",
            background:T.accent, color:"#fff", border:"none",
            fontSize:24, fontWeight:700, cursor:"pointer",
            boxShadow:`0 4px 20px ${T.accent}66, 0 2px 8px rgba(0,0,0,0.2)`,
            display:"flex", alignItems:"center", justifyContent:"center",
            transition:"transform 0.2s, box-shadow 0.2s",
          }}
          onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.1)";}}
          onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";}}
          aria-label="Add Transaction"
        >+</button>
      )}

      {/* Reduce Available Today prompt — shown after marking an expense as paid */}
      <BottomSheet open={!!reduceBalancePrompt} onClose={()=>setReduceBalancePrompt(null)}
        title="💰 Update Available Today?" height="auto">
        {reduceBalancePrompt && (
          <div style={{ padding:"4px 0 12px" }}>
            <div style={{ fontSize:14, color:T.text, lineHeight:1.6, marginBottom:16 }}>
              You marked <strong style={{ color:T.accent }}>{reduceBalancePrompt.label}</strong> as paid.
              <br/>Amount: <strong style={{ color:T.red }}>{currSym()}{reduceBalancePrompt.amount.toLocaleString()}</strong>
            </div>
            <div style={{ background:T.surface, borderRadius:RADIUS.md, padding:"12px 14px", marginBottom:16, border:`1px solid ${T.border}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                <span style={{ fontSize:12, color:T.textSub }}>Current balance</span>
                <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{currSym()}{(availableToday||0).toLocaleString()}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ fontSize:12, color:T.textSub }}>After deduction</span>
                <span style={{ fontSize:14, fontWeight:700, color:((availableToday||0)-reduceBalancePrompt.amount)<0?T.red:T.green }}>{currSym()}{((availableToday||0)-reduceBalancePrompt.amount).toLocaleString()}</span>
              </div>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <Btn onClick={()=>{
                const newBal = (availableToday||0) - reduceBalancePrompt.amount;
                setAvailableToday(newBal, `Expense paid: ${reduceBalancePrompt.label}`);
                setReduceBalancePrompt(null);
              }} color={T.green} style={{ flex:1 }}>
                Yes, reduce by {currSym()}{reduceBalancePrompt.amount.toLocaleString()}
              </Btn>
              <Btn onClick={()=>setReduceBalancePrompt(null)} variant="outline" color={T.textSub} style={{ flex:1 }}>
                No, I'll handle it
              </Btn>
            </div>
            {((availableToday||0)-reduceBalancePrompt.amount)<0 && (
              <div style={{ fontSize:12, color:T.orange, marginTop:8, textAlign:"center" }}>
                ⚠ This will make your Available Today negative
              </div>
            )}
          </div>
        )}
      </BottomSheet>
    </div>
  );
}
